// ============================================
// DATA SERVICE (API) - Backend-backed storage
// ============================================
// Drop-in replacement for data-service.js (Dexie version).
// Same interface, but all operations go through the Fastify API.
// Toggled by VITE_USE_BACKEND=true environment variable.
//
// IMPORTANT: All response bodies are converted snake_case → camelCase after receiving.
// Request bodies remain camelCase because backend routes already map camelCase fields
// to snake_case SQL columns manually. This centralised response translation means
// components get camelCase data regardless of whether it came from Dexie or PostgreSQL.

import { decorateAiError } from './ai-error-handling';
import {
    CLOUD_MEDIA_CLASSES,
    decideCloudMediaUpload,
    expectedCloudMediaKindForEntityType,
} from './cloud-media-policy';
import { resolveReasoningDirective } from './model-config';
import { sanitizeStage5ReadinessReview } from '../utils/stage5-readiness-review.js';
import {
    createServerUnavailableError,
    ensureBackendAnswering,
    isGatewayUnavailableResponse,
    isNetworkFetchFailure,
    isServerUnavailableError,
    isTransientNetworkFailure,
    markBackendReachable,
    markBackendUnreachable,
    normaliseNetworkError,
    withNetworkRetry,
} from './network-resilience';

/**
 * @typedef {import('../types/project-envelope').ProjectEnvelope} ProjectEnvelope
 * @typedef {import('../types/project-envelope').ProjectSaveInput} ProjectSaveInput
 */

const API_BASE = '/api';

// ============================================
// camelCase ↔ snake_case conversion
// ============================================
function toSnakeCase(str) {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function toCamelCase(str) {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function convertKeysToSnake(obj) {
    if (Array.isArray(obj)) return obj.map(convertKeysToSnake);
    if (obj === null || typeof obj !== 'object' || obj instanceof Date) return obj;
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        result[toSnakeCase(key)] = convertKeysToSnake(value);
    }
    return result;
}

/**
 * Top-level snake_case keys in API responses whose VALUES are opaque JSONB
 * documents authored by AI prompts. Their internal field names MUST stay
 * snake_case because every downstream consumer (Stage 2 report, plausibility
 * checks, photo analysis, work-method generator, applied-learnings UI, etc.)
 * reads snake_case fields as contracted with the AI prompt.
 *
 * Before this allowlist existed, `convertKeysToCamel` recursed into these
 * blobs and silently rewrote `detected_risks → detectedRisks`,
 * `confidence_score → confidenceScore`, `chosen_approach → chosenApproach`,
 * making Stage 2 show an empty report, Stage 3 miss work methods, and so on.
 *
 * Bug symptoms this guard fixes (Peter 2026-04-20, "report empty on reload"):
 *   • Stage 2 Risk Scan renders zero risks after save+reload
 *   • Photo findings disappear
 *   • WMS structured.overview goes empty-shape on reconnect
 *   • Applied-learnings citations lose cause_description field
 */
const OPAQUE_JSONB_KEYS = new Set([
    'risk_analysis', 'photo_analysis', 'form_data', 'wms', 'project_strategy',
    'costing_sheet', 'quote_data', 'applied_learnings', 'ai_context',
    'kpi_report', 'project_summary', 'original_ai_scope', 'project_folder',
    'selected_hero_image', 'ai_generated_items',
    // Bio routes store schema-owned field names inside a generic JSONB
    // `data` envelope. Those keys intentionally stay snake_case because
    // BioEditor and prompt formatters address them by schema key.
    'data',
    'work_method_overall_programme',
    // scope items + videos + video_transcripts contain nested metadata that is
    // produced by our own code (not AI prompts) and uses snake_case field
    // names in the backend. Keeping them opaque is safer than case-shifting.
    'scope', 'videos', 'video_transcripts',
    // v5.12.21: documents array holds RFQ / email / notes-source metadata
    // with AI-authored snake_case sub-fields (type, ai_classification,
    // ai_classification_confidence, email_subject, email_from, etc.).
    // Adding it here prevents the response translator from silently
    // mangling `ai_classification` \u2192 `aiClassification` inside the blob.
    // Paired with migration 028 that added the projects.documents column.
    'documents',
    // Receivables Phase 1 (claude/nervous-dewdney-f86dd0, 2026-05-10): the
    // backend route file routes/receivables.js stores Dexie-record fidelity
    // by writing the original camelCase blob into JSONB columns named
    // source / raw / identifiers / signals / reasons / cursor. CSV import
    // headers can contain literally anything (`Invoice #`, `Days_Overdue`,
    // etc.) so any recursive case conversion would mangle the round-trip.
    // Keep them opaque end-to-end. `metadata` is intentionally NOT added
    // here because it is used by other tables (learnings, ai_usage, ...)
    // whose consumers expect camelCase recursion; callers that store
    // receivables metadata write it as camelCase so recursion is a no-op.
    'source', 'raw', 'identifiers', 'signals', 'reasons', 'cursor',
    // Phase 2b.5 (2026-05-15): work_order_drafts.draft holds AI-authored
    // JSON with prompt-contracted field names (subcontractorBrief,
    // materialSchedule, riskCallouts, etc.). The AI prompt emits a
    // camelCase shape but treat it opaque so future prompt revisions can
    // change internal field names without breaking the wire format.
    'draft',
    // Learnings stats aggregates (2026-07-03): the map KEYS are learning
    // type/category NAMES ('scope_template', 'business_rule', ...), i.e.
    // DATA, not columns — recursion would mangle them to 'scopeTemplate'
    // and the AI Training type panels would key-miss. The envelope keys
    // still camelise (by_type -> byType); only the values pass through.
    'by_type', 'by_category',
]);

// `extraOpaqueKeys` is a per-call addition to OPAQUE_JSONB_KEYS (apiFetch's
// `opaqueKeys` option). The Assistant conversation reads use it for
// `messages`: a stored message must round-trip unchanged (spec 2026-10-07,
// F23), but `messages` is too common a key to make opaque for every route.
function convertKeysToCamel(obj, extraOpaqueKeys = null) {
    if (Array.isArray(obj)) return obj.map(item => convertKeysToCamel(item, extraOpaqueKeys));
    if (obj === null || typeof obj !== 'object' || obj instanceof Date) return obj;
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        const camelKey = toCamelCase(key);
        const converted = (OPAQUE_JSONB_KEYS.has(key) || (extraOpaqueKeys && extraOpaqueKeys.has(key)))
            ? value                          // pass opaque blobs through untouched
            : convertKeysToCamel(value, extraOpaqueKeys);
        result[camelKey] = converted;
        // Backward-compat safety net (2026-05-14 — class-of-bug capture, see
        // CLAUDE.md "HARD RULE — apiFetch response keys are camelCase"):
        // when a key actually changed case, also expose the ORIGINAL
        // snake_case key as a NON-ENUMERABLE alias pointing at the same
        // value. Reads work either way; Object.keys/JSON.stringify see only
        // the canonical camelCase name; spread/clone behaviour is unchanged.
        // This stops the recurring "frontend code reads snake_case + silently
        // gets undefined" bug from biting future agents writing fresh route
        // consumers. Canonical pattern is still camelCase — the alias is a
        // forgiveness layer, not a replacement.
        if (camelKey !== key) {
            try {
                Object.defineProperty(result, key, {
                    value: converted,
                    enumerable: false,
                    configurable: true,
                    writable: true,
                });
            } catch { /* shouldn't fire; defensive */ }
        }
    }
    return result;
}

// Test-only export. Lets the unit-test suite verify the bidirectional
// safety-net behaviour without exposing convertKeysToCamel as a public API.
// Not imported by any production code.
export const __testHooks = {
    convertKeysToCamel,
    toCamelCase,
    toSnakeCase,
    OPAQUE_JSONB_KEYS,
    stripInlineMediaForSave: _stripInlineMediaForSave,
    prepareProjectForBackendSave: _prepareProjectForBackendSave,
    backendProjectStatus: _backendProjectStatus,
    withProjectLifecycleAliases: _withProjectLifecycleAliases,
    withProjectSummaryAliases: _withProjectSummaryAliases,
    resetProjectSaveRequestCache: _resetProjectSaveRequestCache,
    ensureProjectMediaDurableForSave: _ensureProjectMediaDurableForSave,
    documentContentType: _documentContentType,
    // Field-hardening timings (refresh timeout, lock wait, no-peer retry pause).
    // Tests shorten them; production never calls this.
    setAuthRefreshTimingForTests: _setAuthRefreshTimingForTests,
};

// ============================================
// JWT token management
// ============================================
const TOKEN_KEY = 'qm_access_token';
const REFRESH_KEY = 'qm_refresh_token';
const AUTH_TOKEN_CHANNEL_NAME = 'qm-auth-token-rotation-v1';
const AUTH_REFRESH_LOCK_NAME = 'qm-auth-refresh-v1';
const AUTH_TOKEN_HANDOFF_WAIT_MS = 150;
const AUTH_REFRESH_PENDING_WAIT_MS = 10 * 1000;
// ─── Field hardening 2026-09-22 (Android / Brave / 4G site inspection) ──────
// A stalled POST /auth/refresh never settled: the AbortController existed but
// nothing ever fired it, so `_refreshPromise` stayed pending and every
// apiFetch in every tab queued behind it through the browser-wide lock. The
// operator's only way out was to log out and back in - three times in 44
// minutes. These bounds are TRANSIENT failures (tokens stay), never logouts.
let AUTH_REFRESH_TIMEOUT_MS = 20 * 1000;
let AUTH_REFRESH_LOCK_WAIT_MS = 20 * 1000;
// A 401 from /auth/refresh with NO peer tab answering is not proof the session
// is dead: on a single-tab phone there are no peers, and the backend now keeps
// a short grace window after a rotation (a just-revoked token presented inside
// it is re-issued). One paused retry with the same token before clearing.
let AUTH_REFRESH_NO_PEER_RETRY_DELAY_MS = 1500;

function _setAuthRefreshTimingForTests({ refreshTimeoutMs, lockWaitMs, noPeerRetryDelayMs } = {}) {
    if (Number.isFinite(refreshTimeoutMs)) AUTH_REFRESH_TIMEOUT_MS = refreshTimeoutMs;
    if (Number.isFinite(lockWaitMs)) AUTH_REFRESH_LOCK_WAIT_MS = lockWaitMs;
    if (Number.isFinite(noPeerRetryDelayMs)) AUTH_REFRESH_NO_PEER_RETRY_DELAY_MS = noPeerRetryDelayMs;
}
const AUTH_TAB_ID = (() => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch { /* fall through */ }
    return `qm-auth-${Date.now()}-${Math.random().toString(36).slice(2)}`;
})();

let _authTokenChannel = null;
let _authStateRevision = 0;
let _activeRefreshAttempt = null;
let _devAutoLoginSuppressed = false;
const _authRecoveryWaiters = new Map();
const _remoteRefreshAttempts = new Map();

function createAuthMessageId(prefix) {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return `${prefix}-${crypto.randomUUID()}`;
        }
    } catch { /* fall through */ }
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readTokenIdentity(token) {
    if (!token || typeof token !== 'string') return null;
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
        const decoded = JSON.parse(atob(padded));
        if (!decoded?.userId || !decoded?.tenantId) return null;
        return { userId: String(decoded.userId), tenantId: String(decoded.tenantId) };
    } catch {
        return null;
    }
}

function tokenIdentityMatches(left, right) {
    const leftIdentity = typeof left === 'string' ? readTokenIdentity(left) : left;
    const rightIdentity = typeof right === 'string' ? readTokenIdentity(right) : right;
    return !!leftIdentity
        && !!rightIdentity
        && leftIdentity.userId === rightIdentity.userId
        && leftIdentity.tenantId === rightIdentity.tenantId;
}

function writeTokensToThisTab(accessToken, refreshToken) {
    if (typeof accessToken === 'string' && accessToken) {
        sessionStorage.setItem(TOKEN_KEY, accessToken);
    }
    if (typeof refreshToken === 'string' && refreshToken) {
        sessionStorage.setItem(REFRESH_KEY, refreshToken);
    }
    _devAutoLoginSuppressed = false;
}

function clearTokensInThisTab({ suppressAutoLogin = false } = {}) {
    _authStateRevision += 1;
    if (_activeRefreshAttempt?.controller) {
        try { _activeRefreshAttempt.controller.abort(); } catch { /* best effort */ }
    }
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    if (suppressAutoLogin) _devAutoLoginSuppressed = true;
}

function wakeAuthRecoveryWaiter(requestId) {
    const waiter = _authRecoveryWaiters.get(requestId);
    if (!waiter?.wake) return;
    const wake = waiter.wake;
    waiter.wake = null;
    wake();
}

function waitForAuthRecoverySignal(waiter, timeoutMs) {
    return new Promise((resolve) => {
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            if (waiter.wake === finish) waiter.wake = null;
            resolve();
        };
        const timer = setTimeout(finish, timeoutMs);
        waiter.wake = finish;
    });
}

function canAdoptPeerTokens(message, expectedRefreshToken) {
    const currentAccessToken = sessionStorage.getItem(TOKEN_KEY);
    const currentRefreshToken = sessionStorage.getItem(REFRESH_KEY);
    return !!currentAccessToken
        && !!currentRefreshToken
        && currentRefreshToken === expectedRefreshToken
        && typeof message?.accessToken === 'string'
        && typeof message?.refreshToken === 'string'
        && !!message.accessToken
        && !!message.refreshToken
        && !isTokenExpired(message.accessToken)
        && tokenIdentityMatches(currentAccessToken, message.accessToken);
}

/**
 * Keep single-use refresh-token rotations coherent across same-origin tabs.
 * sessionStorage is deliberately tab-scoped, but a successful backend refresh
 * revokes the old token globally. Without this in-memory handoff, tab A's
 * perfectly healthy refresh makes tab B's still-open QuoteMaker session look
 * expired on its next request. BroadcastChannel adds no disk persistence and
 * is limited to the same origin already trusted to run QuoteMaker code.
 */
function ensureAuthTokenChannel() {
    if (_authTokenChannel) return _authTokenChannel;
    if (typeof window === 'undefined' || typeof BroadcastChannel !== 'function') return null;
    try {
        const channel = new BroadcastChannel(AUTH_TOKEN_CHANNEL_NAME);
        channel.onmessage = (event) => {
            const message = event?.data;
            if (!message || typeof message !== 'object' || message.sourceTabId === AUTH_TAB_ID) return;
            if (message.type === 'tokens-rotated') {
                // Compare-and-swap: only a tab that still owns the exact token
                // this rotation replaced may adopt the new pair. This prevents
                // delayed broadcasts (or another same-origin login) from
                // overwriting a newer or differently signed-in session.
                if (canAdoptPeerTokens(message, message.replacesRefreshToken)) {
                    writeTokensToThisTab(message.accessToken, message.refreshToken);
                    for (const requestId of _authRecoveryWaiters.keys()) {
                        wakeAuthRecoveryWaiter(requestId);
                    }
                }
                return;
            }
            if (message.type === 'tokens-requested') {
                const accessToken = sessionStorage.getItem(TOKEN_KEY);
                const refreshToken = sessionStorage.getItem(REFRESH_KEY);
                if (!accessToken
                    || !refreshToken
                    || !tokenIdentityMatches(accessToken, message.principal)
                    || typeof message.requestId !== 'string'
                    || typeof message.refreshToken !== 'string') return;

                if (refreshToken !== message.refreshToken && !isTokenExpired(accessToken)) {
                    channel.postMessage({
                        type: 'tokens-recovered',
                        sourceTabId: AUTH_TAB_ID,
                        targetTabId: message.sourceTabId,
                        requestId: message.requestId,
                        requestedRefreshToken: message.refreshToken,
                        accessToken,
                        refreshToken,
                    });
                } else if ((_activeRefreshAttempt?.phase === 'network' || _activeRefreshAttempt?.phase === 'response')
                    && _activeRefreshAttempt.refreshToken === message.refreshToken) {
                    // Web Locks is unavailable in some embedded/older browsers.
                    // Tell the rejected contender that a peer is still waiting
                    // for the same backend rotation; it must not clear the
                    // session while that legitimate winner may still respond.
                    channel.postMessage({
                        type: 'refresh-pending',
                        sourceTabId: AUTH_TAB_ID,
                        targetTabId: message.sourceTabId,
                        requestId: message.requestId,
                        attemptId: _activeRefreshAttempt.id,
                    });
                }
                return;
            }
            if (message.type === 'tokens-recovered') {
                if (message.targetTabId !== AUTH_TAB_ID || typeof message.requestId !== 'string') return;
                if (canAdoptPeerTokens(message, message.requestedRefreshToken)) {
                    writeTokensToThisTab(message.accessToken, message.refreshToken);
                }
                wakeAuthRecoveryWaiter(message.requestId);
                return;
            }
            if (message.type === 'refresh-pending') {
                if (message.targetTabId !== AUTH_TAB_ID || typeof message.requestId !== 'string') return;
                const waiter = _authRecoveryWaiters.get(message.requestId);
                if (waiter && typeof message.attemptId === 'string') {
                    waiter.pendingAttemptIds.add(message.attemptId);
                    wakeAuthRecoveryWaiter(message.requestId);
                }
                return;
            }
            if (message.type === 'refresh-started') {
                const currentAccessToken = sessionStorage.getItem(TOKEN_KEY);
                const currentRefreshToken = sessionStorage.getItem(REFRESH_KEY);
                if (!currentAccessToken
                    || currentRefreshToken !== message.refreshToken
                    || !tokenIdentityMatches(currentAccessToken, message.principal)
                    || typeof message.attemptId !== 'string') return;
                _remoteRefreshAttempts.set(message.attemptId, {
                    refreshToken: message.refreshToken,
                    expiresAt: Date.now() + AUTH_REFRESH_PENDING_WAIT_MS,
                });
                for (const [requestId, waiter] of _authRecoveryWaiters) {
                    if (waiter.requestedRefreshToken === message.refreshToken) {
                        waiter.pendingAttemptIds.add(message.attemptId);
                        wakeAuthRecoveryWaiter(requestId);
                    }
                }
                return;
            }
            if (message.type === 'refresh-settled') {
                _remoteRefreshAttempts.delete(message.attemptId);
                for (const [requestId, waiter] of _authRecoveryWaiters) {
                    if (waiter.pendingAttemptIds.delete(message.attemptId)) {
                        wakeAuthRecoveryWaiter(requestId);
                    }
                }
                return;
            }
            if (message.type === 'session-cleared') {
                const currentAccessToken = sessionStorage.getItem(TOKEN_KEY);
                if (!currentAccessToken || !tokenIdentityMatches(currentAccessToken, message.principal)) return;
                const explicitLogout = message.reason === 'logout';
                if (explicitLogout) {
                    clearTokensInThisTab({ suppressAutoLogin: true });
                    // Token storage is tab-scoped, so App cannot observe this
                    // peer logout through a storage event. Notify the host
                    // immediately so user identity and Pricing authority drop
                    // in the same turn as the coordinated token clear.
                    try {
                        window.dispatchEvent(new CustomEvent('qm:session-cleared', {
                            detail: { reason: 'logout', source: 'peer' },
                        }));
                    } catch { /* token clear remains authoritative */ }
                }
            }
        };
        _authTokenChannel = channel;
        return channel;
    } catch {
        return null;
    }
}

function broadcastTokens(accessToken, refreshToken, replacesRefreshToken) {
    if (!replacesRefreshToken) return;
    try {
        ensureAuthTokenChannel()?.postMessage({
            type: 'tokens-rotated',
            sourceTabId: AUTH_TAB_ID,
            accessToken,
            refreshToken,
            replacesRefreshToken,
        });
    } catch { /* current tab still owns its successful refresh */ }
}

function broadcastSessionCleared({ principal, reason }) {
    if (!principal) return;
    try {
        ensureAuthTokenChannel()?.postMessage({
            type: 'session-cleared',
            sourceTabId: AUTH_TAB_ID,
            principal,
            reason,
        });
    } catch { /* local clear remains authoritative */ }
}

function broadcastRefreshSettled(attempt) {
    if (!attempt?.id) return;
    try {
        ensureAuthTokenChannel()?.postMessage({
            type: 'refresh-settled',
            sourceTabId: AUTH_TAB_ID,
            attemptId: attempt.id,
        });
    } catch { /* waiters retain their bounded fail-safe */ }
}

function broadcastRefreshStarted(attempt, accessToken) {
    const principal = readTokenIdentity(accessToken);
    if (!attempt?.id || !principal) return;
    try {
        ensureAuthTokenChannel()?.postMessage({
            type: 'refresh-started',
            sourceTabId: AUTH_TAB_ID,
            attemptId: attempt.id,
            refreshToken: attempt.refreshToken,
            principal,
        });
    } catch { /* refresh itself remains usable without coordination */ }
}

// Install the listener during application bootstrap, before either tab needs
// its first refresh. Node/test imports remain inert unless they provide a
// browser window and BroadcastChannel explicitly.
ensureAuthTokenChannel();

function getAccessToken() {
    return sessionStorage.getItem(TOKEN_KEY);
}

/**
 * Decode a JWT's payload and check if it's expired.
 * JWTs are self-describing: payload.exp is a unix timestamp (seconds).
 * We add a 30s safety margin so requests in flight don't race the clock.
 *
 * Returns true on any decode failure (treat unparseable tokens as expired).
 *
 * @param {string|null} token
 * @returns {boolean} true if missing, malformed, or expired
 */
function isTokenExpired(token) {
    if (!token || typeof token !== 'string') return true;
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return true;
        // base64url → base64 (replace -/_ and pad with =)
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
        const decoded = JSON.parse(atob(padded));
        if (typeof decoded.exp !== 'number') return true;
        const nowSec = Math.floor(Date.now() / 1000);
        return decoded.exp < nowSec + 30; // 30s safety margin
    } catch {
        return true;
    }
}

// Decode the stored access token's claims (userId / tenantId / role) without
// verifying the signature — display-gating only (e.g. showing the GLOBAL
// learning controls to a platform_admin after a page refresh, where App's
// session-restore previously hardcoded role:'admin'); the backend 403 stays
// the enforcement layer. Returns null when there's no token or it won't parse.
export function getStoredTokenClaims() {
    try {
        const tok = sessionStorage.getItem(TOKEN_KEY);
        if (!tok || typeof tok !== 'string') return null;
        const parts = tok.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
        const decoded = JSON.parse(atob(padded));
        return { userId: decoded.userId, tenantId: decoded.tenantId, role: decoded.role };
    } catch {
        return null;
    }
}

function setTokens(accessToken, refreshToken, { replacesRefreshToken = null } = {}) {
    writeTokensToThisTab(accessToken, refreshToken);
    if (refreshToken && replacesRefreshToken) {
        broadcastTokens(accessToken, refreshToken, replacesRefreshToken);
    }
}

function clearTokens({ reason = 'logout' } = {}) {
    const accessToken = sessionStorage.getItem(TOKEN_KEY);
    const principal = readTokenIdentity(accessToken);
    const explicitLogout = reason === 'logout';
    clearTokensInThisTab({ suppressAutoLogin: explicitLogout });
    // Explicit Sign out is a user intent and must fan out to their other tabs.
    // A rejected refresh is only local evidence: in a lockless race another
    // tab may still be the legitimate slower winner, so never let the loser
    // broadcast a destructive clear.
    if (explicitLogout) {
        broadcastSessionCleared({ principal, reason: 'logout' });
    }
}

function clearInvalidSessionTokens() {
    clearTokens({ reason: 'invalid-session' });
}

// ─── F-02 session-expired event (v5.13.7) ─────────────────────────────────
// Fired exactly once per 30 s window when apiFetch exhausts its refresh +
// re-login retries against a 401. Listeners (App.jsx) surface a sticky toast
// so the user gets explicit "please log in again" feedback instead of the
// silent-swallow that 401/403 unhandledrejection muting would otherwise
// produce. Idempotent so a burst of N parallel in-flight requests doesn't
// stack N toasts.
let _lastSessionExpiredFiredAt = 0;
const _SESSION_EXPIRED_DEDUP_MS = 30 * 1000;
function _maybeFireSessionExpired() {
    if (typeof window === 'undefined') return;
    const now = Date.now();
    if (now - _lastSessionExpiredFiredAt < _SESSION_EXPIRED_DEDUP_MS) return;
    _lastSessionExpiredFiredAt = now;
    try {
        window.dispatchEvent(new CustomEvent('qm:session-expired'));
    } catch {
        // dispatchEvent should never throw, but if a host environment lacks
        // CustomEvent we silently skip — the user still sees their request
        // fail; we just don't get a global banner.
    }
}

let _refreshPromise = null;

async function withBrowserRefreshLock(task) {
    const locks = typeof navigator !== 'undefined' ? navigator?.locks : null;
    if (!locks || typeof locks.request !== 'function') return task();
    let taskStarted = false;
    // Bound the WAIT for the lock. One tab whose refresh had stalled used to
    // hold `qm-auth-refresh-v1` indefinitely, and every other tab's apiFetch
    // sat in this queue with no way out. If the lock is not ours within the
    // deadline we run the refresh without it: the refresh endpoint's own
    // single-use rotation plus the BroadcastChannel recovery already handle a
    // lockless race (that is the "lockless contender" path below).
    const waitController = typeof AbortController === 'function' ? new AbortController() : null;
    const waitTimer = waitController
        ? setTimeout(() => { try { waitController.abort(); } catch { /* best effort */ } }, AUTH_REFRESH_LOCK_WAIT_MS)
        : null;
    try {
        const run = async () => {
            taskStarted = true;
            if (waitTimer) clearTimeout(waitTimer);
            // Let a token-rotation BroadcastChannel event queued by the previous
            // lock owner update this tab before we decide whether another refresh
            // is necessary.
            await new Promise(resolve => setTimeout(resolve, 0));
            return task();
        };
        return await (waitController
            ? locks.request(AUTH_REFRESH_LOCK_NAME, { signal: waitController.signal }, run)
            : locks.request(AUTH_REFRESH_LOCK_NAME, run));
    } catch (error) {
        // A locked-down iframe/webview may expose navigator.locks but reject
        // acquisition, and the wait above may have timed out. Fall back only
        // when our task never started; errors from the refresh itself must
        // retain their original semantics.
        if (!taskStarted) return task();
        throw error;
    } finally {
        if (waitTimer) clearTimeout(waitTimer);
    }
}

function adoptedPeerAccessToken(accessTokenBeforeRefresh, refreshTokenBeforeRefresh) {
    const adoptedAccessToken = getAccessToken();
    const adoptedRefreshToken = sessionStorage.getItem(REFRESH_KEY);
    if (adoptedAccessToken
        && adoptedRefreshToken
        && adoptedRefreshToken !== refreshTokenBeforeRefresh
        && tokenIdentityMatches(accessTokenBeforeRefresh, adoptedAccessToken)
        && !isTokenExpired(adoptedAccessToken)) {
        return adoptedAccessToken;
    }
    return null;
}

async function recoverRotationFromAnotherTab(accessTokenBeforeRefresh, refreshTokenBeforeRefresh) {
    const channel = ensureAuthTokenChannel();
    const principal = readTokenIdentity(accessTokenBeforeRefresh);
    if (!channel || !principal || !refreshTokenBeforeRefresh) {
        return { accessToken: null, peerStillPending: false };
    }
    const requestId = createAuthMessageId('recover');
    const now = Date.now();
    for (const [attemptId, attempt] of _remoteRefreshAttempts) {
        if (attempt.expiresAt <= now) _remoteRefreshAttempts.delete(attemptId);
    }
    const waiter = {
        requestedRefreshToken: refreshTokenBeforeRefresh,
        pendingAttemptIds: new Set(
            [..._remoteRefreshAttempts]
                .filter(([, attempt]) => attempt.refreshToken === refreshTokenBeforeRefresh)
                .map(([attemptId]) => attemptId)
        ),
        wake: null,
    };
    _authRecoveryWaiters.set(requestId, waiter);
    try {
        channel.postMessage({
            type: 'tokens-requested',
            sourceTabId: AUTH_TAB_ID,
            requestId,
            principal,
            refreshToken: refreshTokenBeforeRefresh,
        });
    } catch {
        _authRecoveryWaiters.delete(requestId);
        return { accessToken: null, peerStillPending: false };
    }

    try {
        await waitForAuthRecoverySignal(waiter, AUTH_TOKEN_HANDOFF_WAIT_MS);
        let adoptedAccessToken = adoptedPeerAccessToken(accessTokenBeforeRefresh, refreshTokenBeforeRefresh);
        if (adoptedAccessToken) return { accessToken: adoptedAccessToken, peerStillPending: false };

        const deadline = Date.now() + AUTH_REFRESH_PENDING_WAIT_MS;
        while (waiter.pendingAttemptIds.size > 0) {
            const remainingMs = deadline - Date.now();
            if (remainingMs <= 0) {
                // Ambiguous, not dead: a peer explicitly told us it still has
                // the same rotation in flight. Keep this tab's tokens and let
                // the next request retry rather than wiping a slower winner.
                return { accessToken: null, peerStillPending: true };
            }
            await waitForAuthRecoverySignal(waiter, remainingMs);
            adoptedAccessToken = adoptedPeerAccessToken(accessTokenBeforeRefresh, refreshTokenBeforeRefresh);
            if (adoptedAccessToken) return { accessToken: adoptedAccessToken, peerStillPending: false };
        }
        return { accessToken: null, peerStillPending: false };
    } finally {
        _authRecoveryWaiters.delete(requestId);
    }
}

/**
 * One POST /auth/refresh, bounded. The attempt's AbortController was created
 * long ago but nothing ever fired it, so a refresh that stalled on 4G never
 * settled and `_refreshPromise` pinned every later request behind it. The
 * timer aborts the fetch after AUTH_REFRESH_TIMEOUT_MS and the result is a
 * TRANSIENT error (tokens stay; the next request tries again). An abort that
 * came from clearTokensInThisTab (session changed) is passed through as-is,
 * and a TypeError from fetch is the normalised network error, never a 401.
 */
async function postRefreshRequest(refreshToken, attempt) {
    const controller = attempt.controller;
    let timedOut = false;
    const timer = controller
        ? setTimeout(() => {
            timedOut = true;
            try { controller.abort(); } catch { /* best effort */ }
        }, AUTH_REFRESH_TIMEOUT_MS)
        : null;
    try {
        return await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
            ...(controller ? { signal: controller.signal } : {}),
        });
    } catch (err) {
        if (err?.name === 'AbortError') {
            if (!timedOut) throw err;
            const timeout = new Error(`Token refresh timed out after ${AUTH_REFRESH_TIMEOUT_MS}ms`);
            timeout.name = 'TimeoutError';
            timeout.code = 'AUTH_REFRESH_TIMEOUT';
            timeout.status = 0;
            timeout.definitive = false;
            timeout.transient = true;
            timeout.retryable = true;
            timeout.cause = err;
            throw timeout;
        }
        if (isNetworkFetchFailure(err)) {
            throw normaliseNetworkError(err, { method: 'POST', source: 'refreshAccessToken' });
        }
        throw err;
    } finally {
        if (timer) clearTimeout(timer);
    }
}

async function refreshAccessToken() {
    // Deduplicate concurrent refresh attempts
    if (_refreshPromise) return _refreshPromise;

    const accessTokenBeforeRefresh = getAccessToken();
    const refreshTokenBeforeRefresh = sessionStorage.getItem(REFRESH_KEY);
    const authStateRevisionBeforeRefresh = _authStateRevision;
    _refreshPromise = withBrowserRefreshLock(async () => {
        if (authStateRevisionBeforeRefresh !== _authStateRevision) {
            const err = new Error('Auth session changed while waiting to refresh');
            err.code = 'AUTH_SESSION_CHANGED';
            err.sessionCleared = true;
            throw err;
        }
        // Another tab may have won the same browser-wide refresh lock and
        // handed its new token pair to us while this caller was waiting.
        const adoptedAccessToken = adoptedPeerAccessToken(accessTokenBeforeRefresh, refreshTokenBeforeRefresh);
        if (adoptedAccessToken) return adoptedAccessToken;

        const refreshToken = sessionStorage.getItem(REFRESH_KEY);
        if (!refreshToken) {
            const err = new Error('No refresh token');
            err.definitive = true;
            throw err;
        }

        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const attempt = {
            id: createAuthMessageId('refresh'),
            refreshToken,
            phase: 'network',
            controller,
        };
        _activeRefreshAttempt = attempt;
        broadcastRefreshStarted(attempt, accessTokenBeforeRefresh);

        let noPeerRetryUsed = false;
        try {
            for (;;) {
                const response = await postRefreshRequest(refreshToken, attempt);
                attempt.phase = 'response';

                if (!response.ok) {
                    const body = typeof response.json === 'function'
                        ? await response.json().catch(() => ({}))
                        : {};
                    // The auth backend (its database, its key store) was down
                    // for THIS request. The token has not been judged at all.
                    if (response.status === 503 && body?.code === 'AUTH_BACKEND_UNAVAILABLE') {
                        const err = new Error(body.error || body.message || 'QuoteMaker sign-in service is temporarily unavailable');
                        err.status = 503;
                        err.code = 'AUTH_BACKEND_UNAVAILABLE';
                        err.definitive = false;
                        err.transient = true;
                        err.retryable = true;
                        throw err;
                    }
                    // Only a DEFINITIVE rejection (401/403: the token itself is
                    // invalid, expired, or the account is deactivated) may destroy
                    // the session. A 5xx/502 means the backend was momentarily
                    // unreachable — on the cloud that is exactly a container
                    // restart mid-quote-generation, and clearing here turned every
                    // such blip into a forced logout (Francis's multi-tab report:
                    // "the session logs out and it can't connect to the backend").
                    // Keep the tokens; the next request retries the refresh and
                    // the session survives the outage.
                    let definitive = response.status === 401 || response.status === 403;
                    if (definitive) {
                        attempt.phase = 'recovering';
                        // This request has received its rejection and can no longer
                        // be the peer winner another contender is waiting for.
                        broadcastRefreshSettled(attempt);
                        // A single-use token can be rejected because another active
                        // tab just rotated it. Ask peers for the winning pair before
                        // declaring the user logged out.
                        const recovery = await recoverRotationFromAnotherTab(accessTokenBeforeRefresh, refreshToken);
                        if (recovery.accessToken) return recovery.accessToken;
                        if (recovery.peerStillPending) {
                            definitive = false;
                        } else if (response.status === 401 && !noPeerRetryUsed) {
                            // No peer answered. On a single-tab phone there ARE
                            // no peers, so silence is absence of evidence, not
                            // evidence of death. Pause, then present the same
                            // token once more: the backend's post-rotation grace
                            // window re-issues a pair for a token it revoked
                            // moments ago, and a 401 that was really a dropped
                            // reply on a flaky link comes back 200.
                            noPeerRetryUsed = true;
                            await new Promise(resolve => setTimeout(resolve, AUTH_REFRESH_NO_PEER_RETRY_DELAY_MS));
                            if (authStateRevisionBeforeRefresh !== _authStateRevision) {
                                const err = new Error('Auth session changed while waiting to retry refresh');
                                err.code = 'AUTH_SESSION_CHANGED';
                                err.sessionCleared = !sessionStorage.getItem(REFRESH_KEY);
                                throw err;
                            }
                            const lateAdoptedAccessToken = adoptedPeerAccessToken(accessTokenBeforeRefresh, refreshToken);
                            if (lateAdoptedAccessToken) return lateAdoptedAccessToken;
                            attempt.phase = 'network';
                            broadcastRefreshStarted(attempt, accessTokenBeforeRefresh);
                            continue;
                        } else {
                            clearInvalidSessionTokens();
                        }
                    }
                    const err = new Error('Token refresh failed');
                    err.status = response.status;
                    err.definitive = definitive;
                    err.refreshCoordinationPending = !definitive && (response.status === 401 || response.status === 403);
                    throw err;
                }

                const data = await response.json();
                // A local/remote logout or another successful rotation may happen
                // while fetch is in flight. Never let this older response resurrect
                // a cleared session or overwrite the peer's newer token pair.
                const currentRefreshToken = sessionStorage.getItem(REFRESH_KEY);
                if (authStateRevisionBeforeRefresh !== _authStateRevision || currentRefreshToken !== refreshToken) {
                    const adoptedAccessToken = adoptedPeerAccessToken(accessTokenBeforeRefresh, refreshToken);
                    if (adoptedAccessToken) return adoptedAccessToken;
                    const err = new Error('Auth session changed during refresh');
                    err.code = 'AUTH_SESSION_CHANGED';
                    err.sessionCleared = !currentRefreshToken;
                    throw err;
                }
                if (!tokenIdentityMatches(accessTokenBeforeRefresh, data.accessToken)) {
                    // A refresh token may rotate credentials, never the authenticated
                    // principal or tenant. Refuse a malformed or cross-principal
                    // response before it can select another local cache namespace.
                    clearInvalidSessionTokens();
                    const err = new Error('Token refresh returned a different authenticated identity');
                    err.code = 'AUTH_REFRESH_IDENTITY_MISMATCH';
                    err.definitive = true;
                    throw err;
                }
                setTokens(data.accessToken, data.refreshToken, { replacesRefreshToken: refreshToken });
                return data.accessToken;
            }
        } finally {
            attempt.phase = 'settled';
            broadcastRefreshSettled(attempt);
            if (_activeRefreshAttempt === attempt) _activeRefreshAttempt = null;
        }
    });

    try {
        return await _refreshPromise;
    } finally {
        _refreshPromise = null;
    }
}

// ============================================
// Dev-mode response contract validation (TS-011R)
// ============================================
// Gives the backend contract registry (backend/src/contracts/api-contracts.js,
// 54 Zod schemas / 64 routes) its first runtime consumer. In DEV builds ONLY,
// apiFetch validates each contracted JSON response against its schema and emits
// a single structured console.warn on mismatch. This surfaces frontend↔backend
// contract drift the moment it lands, instead of letting a renamed/dropped
// field silently degrade a downstream consumer (the class-of-bug the camelCase
// HARD RULE and the convertKeysToCamel alias safety net both fight).
//
// Hard guarantees (binding):
//   • DEV-only. `import.meta.env.DEV` is statically `false` in prod builds, so
//     Vite/Rollup eliminates this whole branch — INCLUDING the dynamic import
//     below — from the production bundle. The backend module (and the zod it
//     pulls) never reach prod.
//   • Fail-open + warn-only. Validation NEVER throws, NEVER alters the response,
//     NEVER blocks the user flow. Any error in the validation path is swallowed.
//   • Wire-shape validation. The check runs against the RAW JSON body BEFORE
//     convertKeysToCamel, because the schemas model the snake_case wire shape.
//   • Kill switch. `localStorage.qm_disable_api_contract_validation = 'true'`
//     turns it off (default: validation ON in dev).
//   • Import once per session. The dynamic import promise is memoised.

// Test seam: the unit suite injects a fake loader so it can assert behaviour
// without truly importing backend code (and to prove the loader is NOT called
// on unmatched routes / when disabled). Production leaves this null and uses
// the real dev-guarded dynamic import.
let _contractRegistryLoaderForTests = null;
let _contractRegistryPromise = null;

/**
 * Memoised loader for the backend contract registry. Returns the module's
 * `findContractSchema` (or null if the import fails — fail-open).
 *
 * The dynamic import lives INSIDE the `import.meta.env.DEV` guard at the call
 * site (validateResponseContract), so Rollup statically prunes it in prod. We
 * still re-assert DEV here as defence-in-depth.
 *
 * @returns {Promise<((method: string, url: string) => unknown) | null>}
 */
function _loadContractRegistry() {
    if (_contractRegistryLoaderForTests) {
        // Test-injected loader fully replaces the dynamic import.
        return Promise.resolve(_contractRegistryLoaderForTests());
    }
    if (_contractRegistryPromise) return _contractRegistryPromise;
    _contractRegistryPromise = import('../../backend/src/contracts/api-contracts.js')
        .then((mod) => (typeof mod.findContractSchema === 'function' ? mod.findContractSchema : null))
        .catch((err) => {
            // Fail-open: a failed import must not break the dev request path.
            // Warn once so the seam itself is observable if it ever breaks.
            try {
                console.warn('[api-contract-validation] registry import failed; validation disabled this session:', err?.message || err);
            } catch { /* never throw from the failure path */ }
            return null;
        });
    return _contractRegistryPromise;
}

/**
 * Test-only: inject a synchronous loader that returns a `findContractSchema`
 * function (or null). Pass null to restore the real dynamic-import loader and
 * reset the memoised promise. Not used by any production code.
 *
 * @param {(() => ((method: string, url: string) => unknown) | null) | null} loader
 */
export function _setContractRegistryLoaderForTests(loader) {
    _contractRegistryLoaderForTests = loader;
    _contractRegistryPromise = null; // reset memoisation between test cases
}

/**
 * Dev-only, fail-open contract validation of a raw wire response body.
 * Resolves a schema via findContractSchema(method, url); if matched, runs
 * safeParse and emits exactly ONE structured console.warn on mismatch. Returns
 * a promise that always resolves (never rejects) and is awaited purely so the
 * unit suite is deterministic — it does not gate or mutate the response.
 *
 * @param {string} method - HTTP method (defaults to GET upstream)
 * @param {string} url - FULL path incl /api prefix (findContractSchema's contract)
 * @param {unknown} wireData - the raw parsed JSON body (pre-camelCase)
 */
async function validateResponseContract(method, url, wireData) {
    try {
        // Kill switch (dev-time escape hatch; default validation ON).
        try {
            if (typeof localStorage !== 'undefined'
                && localStorage.getItem('qm_disable_api_contract_validation') === 'true') {
                return;
            }
        } catch { /* localStorage may throw in some sandboxes; treat as enabled */ }

        const findContractSchema = await _loadContractRegistry();
        if (typeof findContractSchema !== 'function') return;

        const schema = findContractSchema(method || 'GET', url);
        if (!schema || typeof schema.safeParse !== 'function') return; // uncontracted route → skip

        const result = schema.safeParse(wireData);
        if (result.success) return;

        // Summarise the zod issues compactly: "path: message" per issue, capped.
        const issues = (result.error?.issues || []).slice(0, 8).map((i) => {
            const path = Array.isArray(i.path) && i.path.length ? i.path.join('.') : '(root)';
            return `${path}: ${i.message}`;
        });
        const extra = (result.error?.issues || []).length - issues.length;
        console.warn(
            `[api-contract-validation] response shape mismatch for ${String(method || 'GET').toUpperCase()} ${url}`,
            {
                route: `${String(method || 'GET').toUpperCase()} ${url}`,
                issues,
                ...(extra > 0 ? { additionalIssues: extra } : {}),
            },
        );
    } catch {
        // Fail-open: validation must NEVER break the request path.
    }
}

// ============================================
// Core fetch with JWT + case conversion
// ============================================

/**
 * The one place apiFetch touches the network. A fetch that never produced an
 * HTTP response (Brave: `TypeError: Failed to fetch`) used to propagate bare,
 * with no status, no code and no body, and every surface printed it verbatim.
 * It is rethrown as the normalised network error: status 0, code
 * NETWORK_UNREACHABLE, and `outcomeUnknown` for anything but GET/HEAD because
 * the server MAY have applied the request and lost the reply (the site visit
 * that "did not send" while the office docket showed it submitted).
 * A caller's own AbortError passes through untouched.
 */
async function fetchOrNetworkError(url, init, method) {
    try {
        return await fetch(url, init);
    } catch (err) {
        if (err?.name === 'AbortError') throw err;
        if (isNetworkFetchFailure(err)) {
            // No HTTP response at all: the server did not answer this browser.
            // Say so to the whole app (banner, autosave pause, background
            // gates); the recovery loop probes /api/health and clears it.
            markBackendUnreachable();
            throw normaliseNetworkError(err, { method, source: 'apiFetch' });
        }
        throw err;
    }
}

/**
 * Opt-in retry for requests the caller asserts are safe to repeat. apiFetch
 * never retries on its own: a POST that reached the server and lost its reply
 * would be applied twice. Callers pass `retry: { attempts, timeoutMs }` on
 * idempotent reads; on any other method they must also say
 * `idempotent: true`, otherwise the option is ignored (and warned about in
 * dev) rather than silently honoured. NEVER pass this on an AI pipeline call.
 */
function resolveApiFetchRetry(retry, method) {
    if (!retry || typeof retry !== 'object') return null;
    const safeMethod = method === 'GET' || method === 'HEAD';
    if (!safeMethod && retry.idempotent !== true) {
        if (import.meta.env.DEV) {
            console.warn(`[apiFetch] retry ignored on ${method}: pass retry.idempotent = true only when the request is safe to repeat.`);
        }
        return null;
    }
    const attempts = Number.isInteger(retry.attempts) && retry.attempts > 0 ? retry.attempts : 3;
    const timeoutMs = Number.isFinite(retry.timeoutMs) && retry.timeoutMs >= 0 ? retry.timeoutMs : 0;
    return { attempts, timeoutMs };
}

async function apiFetch(path, options = {}) {
    const { retry, opaqueKeys, ...requestOptions } = options || {};
    const method = String(requestOptions.method || 'GET').toUpperCase();
    const retryPlan = resolveApiFetchRetry(retry, method);
    const extraOpaqueKeys = Array.isArray(opaqueKeys) && opaqueKeys.length ? new Set(opaqueKeys) : null;
    if (!retryPlan) return apiFetchOnce(path, requestOptions, method, extraOpaqueKeys);
    return withNetworkRetry(
        (attemptSignal) => apiFetchOnce(path, {
            ...requestOptions,
            ...(attemptSignal ? { signal: attemptSignal } : {}),
        }, method, extraOpaqueKeys),
        {
            attempts: retryPlan.attempts,
            timeoutMs: retryPlan.timeoutMs,
            signal: requestOptions.signal,
            shouldRetry: isTransientNetworkFailure,
        },
    );
}

/**
 * A status from POST /auth/refresh that says the server could not answer the
 * refresh yet (a 5xx it wrote, rate limiting, a request timeout). Not a
 * verdict on the refresh token: only 401/403 are.
 */
function isUnansweredRefreshStatus(status) {
    return typeof status === 'number' && (status >= 500 || status === 429 || status === 408);
}

async function apiFetchOnce(path, options = {}, method = String(options.method || 'GET').toUpperCase(), extraOpaqueKeys = null) {
    const url = `${API_BASE}${path}`;
    // Only set Content-Type for requests that carry a body (POST/PUT/PATCH).
    // DELETEs and GETs with no body must NOT send Content-Type: application/json
    // or Fastify rejects them with "Body cannot be empty when content-type is
    // set to 'application/json'" - which silently broke project deletion.
    const headers = { ...options.headers };
    const bodyIsFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    if (options.body
        && !bodyIsFormData
        && !headers['Content-Type']
        && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
    }

    // Proactive token refresh - if the access token is already expired (by
    // its own exp claim), refresh BEFORE making the request. This avoids the
    // devtools 401 noise that scared users into thinking the app was broken,
    // without changing functional behaviour (the catch-below path still handles
    // server-side revocation).
    let token = getAccessToken();
    if (token && isTokenExpired(token)) {
        try {
            token = await refreshAccessToken();
        } catch {
            // Refresh failed - fall through; the 401 catch below will attempt
            // re-login as the last resort.
            token = getAccessToken();
        }
    }
    if (!token) {
        try {
            // Local installer/prod-style builds can auto-login, but doing it
            // only after the first protected request creates a scary red 401
            // in DevTools. Try the local auth path before the request so
            // harmless startup calls like /tenant-profile do not look broken.
            await devAutoLogin();
            token = getAccessToken();
        } catch {
            token = getAccessToken();
        }
    }
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let response = await fetchOrNetworkError(url, { ...options, headers }, method);

    // On 401, try refreshing the token, then re-login as a last resort
    if (response.status === 401) {
        // Whether the session is PROVEN dead (server rejected the refresh
        // token, or rejected a freshly-refreshed access token) versus merely
        // unreachable. Only the proven case may destroy the stored tokens —
        // clearing on a transient 5xx turned every cloud container restart
        // mid-generation into a forced logout across the affected tabs.
        let sessionProvenDead = !token; // no token at all → bootstrap path
        // Step 1: try token refresh (if we had a token)
        if (token) {
            try {
                const newToken = await refreshAccessToken();
                headers['Authorization'] = `Bearer ${newToken}`;
                response = await fetchOrNetworkError(url, { ...options, headers }, method);
                if (response.ok || response.status === 204) {
                    // Refresh worked - continue to response handling below
                } else if (response.status === 401) {
                    // The server rejected an access token it just issued —
                    // the session is genuinely dead (e.g. secret rotation).
                    sessionProvenDead = true;
                } else {
                    // Non-401 error - let normal error handling below deal with it
                }
            } catch (refreshErr) {
                // The refresh never reached the backend (network dropped or
                // the bounded refresh timed out). The 401 we hold is stale
                // evidence: the truthful error for THIS request is "could not
                // be reached", and the request itself was refused before it
                // was applied, so the outcome is known. Never a logout.
                if (refreshErr?.code === 'NETWORK_UNREACHABLE' || refreshErr?.code === 'AUTH_REFRESH_TIMEOUT') {
                    const unreachable = normaliseNetworkError(refreshErr, { method: 'GET', source: 'apiFetch' });
                    unreachable.outcomeUnknown = false;
                    throw unreachable;
                }
                // The refresh reached a gateway or an auth service that was
                // down (502/503/504, AUTH_BACKEND_UNAVAILABLE). Same verdict:
                // nothing judged the token, so this is "the server is not
                // answering", never a 401. A boot identity check that read the
                // 401 here signed the operator out mid-deploy (2026-09-29).
                if (isServerUnavailableError(refreshErr)) {
                    throw createServerUnavailableError({ label: 'Signed-in request', method, status: 503, cause: refreshErr });
                }
                // The refresh reached the server, which could not answer it
                // yet: a 500 (the refresh route answers 500 for any error, a
                // database pool timeout included), a 429 or a 408. Nothing
                // judged the token, so the 401 we hold for the expired access
                // token is stale. When a deploy finished and every tab
                // reconnected at once, the boot check read that 401 as
                // "signed out" and deleted a refresh token the server still
                // held. Same verdict as above: try again, keep the session.
                if (!refreshErr?.definitive && isUnansweredRefreshStatus(refreshErr?.status)) {
                    throw createServerUnavailableError({ label: 'Signed-in request', method, status: 503, cause: refreshErr });
                }
                // Only a definitive 401/403 from the refresh endpoint proves
                // the session dead; anything else is an outage to ride out.
                sessionProvenDead = !!(refreshErr && refreshErr.definitive);
            }
        }
        // Step 2: only when the session is PROVEN dead, clear the stale
        // tokens and re-login (dev mode only). A 401 whose refresh merely
        // failed to reach the backend keeps its tokens — the next request
        // retries the refresh and the session survives the outage.
        if (response.status === 401 && sessionProvenDead) {
            clearInvalidSessionTokens();
            try {
                await devAutoLogin();
                const freshToken = getAccessToken();
                if (freshToken) {
                    headers['Authorization'] = `Bearer ${freshToken}`;
                    response = await fetchOrNetworkError(url, { ...options, headers }, method);
                }
            } catch {
                // Re-login failed - let the 401 propagate
            }
        }
    }

    if (response.ok) {
        // Any 2xx from any route is proof the backend answers. The banner
        // state used to be clearable only by the DataService proxy, so a
        // surface that calls apiFetch directly (Site Visits) could work for an
        // hour under a red "Backend unreachable" that nothing would clear.
        markBackendReachable();
    }

    if (!response.ok) {
        let bodyWasJson = true;
        const error = await response.json().catch(() => {
            bodyWasJson = false;
            return { message: response.statusText };
        });
        // A load balancer or proxy answered for the backend (HTML 502/503/504:
        // nothing behind it). The backend itself always answers JSON, so a
        // 503 it wrote on purpose never lands here.
        const gatewayUnavailable = isGatewayUnavailableResponse(response.status, bodyWasJson);
        if (gatewayUnavailable) markBackendUnreachable();
        const message = error.message || error.error || error.detail || response.statusText || `API error: ${response.status}`;
        const err = new Error(message);
        if (gatewayUnavailable) err.gatewayUnavailable = true;
        // Attach the HTTP status so callers can distinguish 404 (data absence)
        // from 5xx (backend failure) without parsing the message. The proxy
        // in data-service.js uses err.status to decide whether to count a
        // failure against the backend-health threshold. Peter 2026-04-20.
        err.status = response.status;
        // `detail` is the backend's SECOND sentence: what to do about it. The
        // line above only folds it into the message when there is no `error`
        // field, which for this API is almost never, so a route that carefully
        // explained the fix was having that explanation discarded here.
        //
        // Found on the cloud: the operator console answered 503 with
        // "not configured on this install" plus a detail naming the one secret
        // to set, and the screen showed only the first sentence. Peter's
        // reasonable conclusion was that he had signed in as the wrong person.
        //
        // Attached rather than concatenated, so a caller can render it as its
        // own line (the pattern the console pages already use) without having
        // to split a string.
        if (error.detail) err.detail = error.detail;
        // Fastify routes historically use either `code` or `error` for the
        // stable machine identifier. Duplicate rollback must not mistake a
        // received 409 refusal for an ambiguous lost response merely because
        // the identifier arrived in `error`. Promote only code-shaped values;
        // human prose in `error` remains message/body data.
        const routeErrorCode = error.code || (
            typeof error.error === 'string' && /^[A-Z][A-Z0-9_]{2,127}$/.test(error.error)
                ? error.error
                : null
        );
        if (routeErrorCode) err.code = routeErrorCode;
        // The auth backend was unavailable for THIS request (its database or
        // key store was down). No credential was judged, so this is transient
        // by definition: tokens stay, no session-expired event, and a caller
        // reading `transient` knows to try again rather than send the operator
        // to the login page.
        if (response.status === 503 && err.code === 'AUTH_BACKEND_UNAVAILABLE') {
            err.transient = true;
            err.definitive = false;
            err.retryable = true;
        }
        if (error.category) err.category = error.category;
        if (error.feature) err.feature = error.feature;
        if (error.model) err.model = error.model;
        if (error.operatorAction) err.operatorAction = error.operatorAction;
        if (typeof error.retryable === 'boolean') err.retryable = error.retryable;
        if (error.retryAfterSeconds) err.retryAfterSeconds = error.retryAfterSeconds;
        if (error.policyBlocked) err.policyBlocked = true;
        if (error.upstreamStatusCode) err.upstreamStatusCode = error.upstreamStatusCode;
        // v5.36.23: attach the full parsed body as err.body so callers can
        // surface structured 409/422 data (e.g. existingJob from intake) that
        // doesn't fit the well-known top-level field whitelist above.
        err.body = error;
        decorateAiError(err);
        // F-02 (v5.13.7): tag the error with its origin so the boot-time
        // unhandledrejection swallow (silence-pre-auth-rejections.js) can
        // narrow its filter to ONLY apiFetch-origin 401/403s. Other 401/403s
        // (e.g. genuine session expiry surfaced from a non-apiFetch path)
        // pass through to the toast banner. Without this tag the swallow
        // hid every 401/403 app-wide and broke session-expiry UX.
        err.qmSource = 'apiFetch';
        // F-02 (v5.13.7): when the access token is gone after retry +
        // re-login, fire a single qm:session-expired CustomEvent so App.jsx
        // can surface a sticky "please log in again" toast. Idempotent over
        // a 30 s window so a burst of in-flight requests doesn't pile toasts.
        if (response.status === 401 && !getAccessToken()) {
            try { _maybeFireSessionExpired(); } catch { /* never block error path */ }
        }
        throw err;
    }

    if (response.status === 204) return null;
    const data = await response.json();

    // TS-011R: dev-only, fail-open contract validation against the raw WIRE
    // body BEFORE camelCasing (the schemas model the snake_case wire shape).
    // Guarded by `import.meta.env.DEV` so the dynamic import + zod are pruned
    // from the prod bundle. Awaited only for deterministic tests; it never
    // throws, never mutates `data`, never gates the return.
    if (import.meta.env.DEV) {
        await validateResponseContract(options.method, url, data);
    }

    return convertKeysToCamel(data, extraOpaqueKeys);
}

// Helper for POST/PUT with body
// NOTE: Request bodies are sent as camelCase because backend routes already
// manually map camelCase fields to snake_case SQL columns. Only RESPONSES
// need conversion (snake_case → camelCase), which apiFetch handles above.
function post(path, body) {
    return apiFetch(path, { method: 'POST', body: JSON.stringify(body) });
}
function put(path, body) {
    return apiFetch(path, { method: 'PUT', body: JSON.stringify(body) });
}
function patch_(path, body) {
    return apiFetch(path, { method: 'PATCH', body: JSON.stringify(body) });
}
function del(path) { return apiFetch(path, { method: 'DELETE' }); }

// ============================================
// Dev auto-login - called once when backend mode activates
// ============================================
// When VITE_USE_BACKEND=true and no token exists, explicit non-Docker dev
// builds may authenticate with configured local credentials. Hosted and
// prod-style Docker builds leave VITE_DEV_AUTH_* blank and fall through to
// LoginPage; do not add source-code credential fallbacks here.
// v5.13.7: concurrency dedup + back-off for devAutoLogin. Multiple parallel
// apiFetch calls during boot used to each invoke devAutoLogin independently
// — N=20+ login attempts would race, trip the IP rate-limiter on the auth
// endpoint, and lock the dev out for ~50 minutes. The dedup here mirrors the
// _refreshPromise pattern: while a login is in flight, every other caller
// awaits the same promise. The back-off prevents tight retry loops after a
// hard failure (429 / 500): subsequent calls within _DEV_LOGIN_BACKOFF_MS
// short-circuit instead of hammering the endpoint again. The user can clear
// the back-off by calling devAutoLogin() with no recent failure or by
// reloading the page (state is module-scoped, not persisted).
let _devLoginPromise = null;
let _devLoginBackoffUntil = 0;
const _DEV_LOGIN_BACKOFF_MS = 30 * 1000; // 30 s after a failed attempt

async function devAutoLogin() {
    // An explicit local/remote Sign out must win over requests that were
    // already in flight. The next full login-page reload resets module state;
    // until then, background work cannot silently authenticate the user again.
    if (_devAutoLoginSuppressed) return;
    // De-dup: in-flight login → join it.
    if (_devLoginPromise) return _devLoginPromise;
    // Back-off: recent failure → no-op so we don't hammer the rate-limiter.
    if (Date.now() < _devLoginBackoffUntil) return;

    _devLoginPromise = _devAutoLoginInner();
    try {
        await _devLoginPromise;
    } finally {
        _devLoginPromise = null;
    }
}

async function _devAutoLoginInner() {
    // ─── Three-tier token resolution (cheapest → most expensive) ──────────
    // Earlier this function went straight to a network probe + full re-login
    // for every stale token, which (a) wasted a round-trip when the token was
    // still valid by exp claim, and (b) wasted a re-login when the refresh
    // token was perfectly good. Both inefficiencies were silently masked by
    // a red 401 in the console. The proper fix walks the cheap-to-expensive
    // tiers in order:
    //
    //   Tier 1: Decode JWT exp claim client-side → no network at all
    //   Tier 2: Refresh access token via /auth/refresh → one round-trip
    //   Tier 3: Full re-login with dev credentials → two round-trips + DB hit
    //
    // The /auth/verify endpoint is kept as a defensive last resort for cases
    // where the access token claims to be unexpired but the server has
    // revoked it (logout, key rotation, user deactivated). We probe it only
    // if exp is in the future and refresh isn't available.
    const existingToken = getAccessToken();
    if (existingToken) {
        // Tier 1: client-side exp check - ZERO network calls
        if (!isTokenExpired(existingToken)) {
            // F-08 (v5.13.7): a valid unexpired token means auth is fine
            // RIGHT NOW. Clear any stale back-off so a previously-armed
            // failure window can't keep blocking subsequent /auth/login
            // attempts when they actually need to run.
            _devLoginBackoffUntil = 0;
            return;
        }

        // Tier 2: token is expired by exp; try refresh before giving up
        const refreshToken = sessionStorage.getItem(REFRESH_KEY);
        if (refreshToken) {
            try {
                await refreshAccessToken();
                // F-08: refresh succeeded — reset back-off (see Tier 1 note).
                _devLoginBackoffUntil = 0;
                return; // Refresh worked - fresh access token is now in storage
            } catch (err) {
                // Only a backend-proven 401/403 may destroy this session and
                // fall through to dev credentials. Outages, peer coordination,
                // and an explicit logout all leave this path without mutation.
                if (!err?.definitive) return;
                clearInvalidSessionTokens();
            }
        } else {
            // Access token expired and no refresh token to use
            clearInvalidSessionTokens();
        }
    }

    // ─── Auto-login gate ────────────────────────────────────────────────────
    // We auto-login in two scenarios:
    //   1. Vite dev mode (`import.meta.env.DEV`) - traditional developer flow.
    //   2. Any prod build served from localhost / 127.0.0.1 / 192.168.x - i.e.
    //      Peter's local Docker container at :8888. This is the Chrome App
    //      path. Critically, this does NOT enable auto-login in a cloud /
    //      public-network build because the hostname check fails for any
    //      non-private address.
    //
    // Hosted/prod-style Docker builds intentionally omit VITE_DEV_AUTH_* so no
    // admin password is embedded in browser JS or retained in Docker metadata.
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalOrigin = hostname === 'localhost'
        || hostname === '127.0.0.1'
        || hostname.startsWith('192.168.');
    if (!import.meta.env.DEV && !isLocalOrigin) return;

    // Vite embeds VITE_* values into browser-visible JavaScript, so this is an
    // explicit local-only opt-in rather than a source-code fallback.
    const email = import.meta.env.VITE_DEV_AUTH_EMAIL || '';
    const password = import.meta.env.VITE_DEV_AUTH_PASSWORD || '';
    if (!email || !password) {
        _devLoginBackoffUntil = Date.now() + _DEV_LOGIN_BACKOFF_MS;
        return;
    }

    console.log('[QM] No auth token - attempting dev auto-login...');
    try {
        const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            // v5.13.7: arm the back-off so we don't hammer the auth endpoint
            // and trip the IP rate-limiter (which locks the dev out for
            // ~50 min). The promise still resolves cleanly so awaiting
            // callers don't hang.
            _devLoginBackoffUntil = Date.now() + _DEV_LOGIN_BACKOFF_MS;
            throw new Error(err.message || `HTTP ${response.status}`);
        }
        const data = await response.json();
        setTokens(data.accessToken, data.refreshToken);
        // Successful login clears any prior back-off so the next 401 (after
        // token expiry, say) can re-authenticate immediately.
        _devLoginBackoffUntil = 0;
        console.log('[QM] Dev auto-login OK:', data.user?.name || data.user?.email);
    } catch (err) {
        if (_devLoginBackoffUntil <= Date.now()) {
            // Network-class failure (fetch threw before we got a Response)
            // — also arm the back-off so a downed backend doesn't get
            // pummelled by every component re-render.
            _devLoginBackoffUntil = Date.now() + _DEV_LOGIN_BACKOFF_MS;
        }
        console.warn('[QM] Dev auto-login failed (is the backend running?):', err.message);
    }
}

/**
 * Like apiFetch, but returns the raw Response object instead of parsed JSON.
 * Use this for endpoints that return non-JSON bodies — currently the PDF
 * render endpoint at /api/quotes/render-pdf, which returns
 * application/pdf bytes that the caller pipes to .blob().
 *
 * Does the same proactive-refresh + 401-retry + dev-relogin dance as apiFetch
 * so callers don't get a stale-token 401 from a long-running session. Caught
 * 2026-05-22 E2E run: Download PDF hit a 401 because the raw fetch at
 * FullQuoteEditorModal.jsx:3387 attached the token but never refreshed on 401.
 *
 * Throws on network failure; returns Response on every HTTP outcome (caller
 * checks .ok / .status). DOES NOT throw on !ok — same shape as window.fetch.
 *
 * @param {string} path - API path WITHOUT the `/api` prefix (the helper
 *   prepends API_BASE itself, matching apiFetch's contract). A leading
 *   `/api/` is stripped defensively with a console.warn so a future
 *   regression surfaces immediately instead of silently 404-ing
 *   like the 2026-05-23 PDF-download regression did.
 * @param {RequestInit} options - standard fetch options
 * @returns {Promise<Response>}
 */
async function fetchWithAuthRefresh(path, options = {}) {
    // 2026-05-23 hardening: detect the double-prefix mistake that produced
    // `/api/api/quotes/render-pdf` → 404 across the Download PDF flow.
    // The original raw `fetch('/api/quotes/render-pdf', ...)` call used an
    // absolute path; the migrated callers should pass `/quotes/render-pdf`
    // because this helper prepends API_BASE. Strip a leading `/api/` and
    // warn so the regression is loud, not silent.
    let normalisedPath = path;
    if (typeof normalisedPath === 'string' && /^\/api(\/|$)/.test(normalisedPath)) {
        console.warn(
            `[fetchWithAuthRefresh] path "${normalisedPath}" includes the /api prefix; the helper prepends API_BASE itself. Stripping the duplicate so the URL is correct. Update the caller to pass the path WITHOUT /api.`
        );
        normalisedPath = normalisedPath.replace(/^\/api/, '') || '/';
    }
    const url = normalisedPath.startsWith('http') ? normalisedPath : `${API_BASE}${normalisedPath}`;
    const headers = { ...options.headers };
    if (options.body && !headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
    }

    let token = getAccessToken();
    if (token && isTokenExpired(token)) {
        try { token = await refreshAccessToken(); }
        catch { token = getAccessToken(); }
    }
    if (!token) {
        try { await devAutoLogin(); token = getAccessToken(); }
        catch { token = getAccessToken(); }
    }
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
        let sessionProvenDead = !token;
        if (token) {
            try {
                const newToken = await refreshAccessToken();
                headers['Authorization'] = `Bearer ${newToken}`;
                response = await fetch(url, { ...options, headers });
                if (response.status === 401) sessionProvenDead = true;
            } catch (refreshErr) {
                sessionProvenDead = !!refreshErr?.definitive;
            }
        }
        if (response.status === 401 && sessionProvenDead) {
            clearInvalidSessionTokens();
            try {
                await devAutoLogin();
                const freshToken = getAccessToken();
                if (freshToken) {
                    headers['Authorization'] = `Bearer ${freshToken}`;
                    response = await fetch(url, { ...options, headers });
                }
            } catch { /* let the 401 propagate */ }
        }
    }

    return response;
}

// Export for auto-login and other low-level callers. Developer pipeline
// history/export surfaces use the bounded methods on DataServiceAPI.pipelines
// below so page limits and cursor encoding cannot drift between components.
export { setTokens, clearTokens, getAccessToken, refreshAccessToken, devAutoLogin, apiFetch, fetchWithAuthRefresh };

// ============================================
// QUOTES
// ============================================
const quotes = {
    async getAll() { return apiFetch('/quotes'); },
    async save(quote) {
        if (quote.id && isUUID(quote.id)) return put(`/quotes/${quote.id}`, quote);
        const { id, ...rest } = quote; return post('/quotes', rest);
    },
    async delete(id) { return del(`/quotes/${id}`); }
};

// ============================================
// DRAFTS
// ============================================
const drafts = {
    async get() { return apiFetch('/drafts/current').catch(() => null); },
    async save(draft) { return post('/drafts', draft); },
    async clear() { return del('/drafts/current'); }
};

// ============================================
// PROJECTS
// ============================================

// v5.41.3 hot-fix (2026-05-24): defensive last-mile strip + size guard.
// Customer Zero hit `413 Payload Too Large` saving projects because inline
// base64 photos / PDFs were leaking through the multi-layer strip chain.
// Root-cause fixes landed in App.jsx + photo-persistence.js; this is the
// safety net that catches every save path (App.jsx state-builder, batch
// queue, assistant, etc.) and prevents bloat from reaching the wire.
//
// The strip rules preserve every durable server/local reference and mark an
// unbacked photo as local-only instead of sending its bytes. Stage2RiskScan.jsx
// re-fetches PDF previews via pdfS3Key/fileMetadataId; photo-auto-assign's
// resolvePhotoDataUrl falls back through blob store + s3Key. The backend JSON
// remains portable while local upload/retry and previews keep their sources.
const PAYLOAD_WARN_THRESHOLD_BYTES = 10 * 1024 * 1024;   // 10 MB → log breakdown
const PAYLOAD_HARD_FAIL_BYTES      = 48 * 1024 * 1024;   // 48 MB → fail with helpful msg (backend bodyLimit is 50 MB)

function _isDataUrl(s) {
    return typeof s === 'string' && s.startsWith('data:') && s.includes(';base64,');
}

function _isBrowserBlobUrl(value) {
    return typeof value === 'string' && /^\s*blob:/i.test(value);
}

const STRIPPED_CUSTOMER_MEDIA = Symbol('stripped-customer-media');

// Keep this portable scrub aligned with the backend's fail-closed
// inline-customer-media-policy. The browser used to remove only explicit
// `data:image/*` / `data:video/*` URLs. That left three representations able to
// reach POST /projects during an async Stage 1 import: a media payload disguised
// as application/octet-stream, a bare `thumbnailBase64`/proxy payload, and a
// serialised byte container in a nested processing snapshot. The server rightly
// refused all three, but the whole fresh-project save then failed repeatedly.
//
// This remains a projection-only operation. The live project object and its
// browser-local Blob/IndexedDB copies are not mutated; only the JSON wire copy
// drops values the backend is contractually forbidden to persist.
const _GENERIC_DATA_URL_RE = /^\s*data:([^,]*),(.*)$/is;
const _MAX_OPAQUE_INLINE_STRING_CHARS = 128 * 1024;
const _MAX_OPAQUE_BYTE_ARRAY_LENGTH = 4096;
const _BASE64_RUN_RE = /[A-Za-z0-9+/_-]{32,}={0,2}/g;
const _HEX_RUN_RE = /(?:[0-9a-fA-F]{2}){16,}/g;
const _EMBEDDED_DATA_URL_TOKEN_RE = /data:([^,\s"'<>]*),([A-Za-z0-9+/_=-]+)/gi;
const _MEDIA_DATA_URL_ANYWHERE_RE = /data:(?:image|video)\/[^,\s"'<>]*,/i;
const _WHOLE_MEDIA_DATA_URL_RE = /^\s*data:(?:image|video)\/[^,\s"'<>]*,/i;
const _QUOTED_MEDIA_DATA_URL_RE = /(["'])([ \t]*)(data:(?:image|video)\/[^,\s"'<>]*,[\s\S]*?)\1/gi;
const _FOLDED_UNQUOTED_MEDIA_DATA_URL_RE = /data:(?:image|video)\/[^,\s"'<>]*,[A-Za-z0-9+/_=-]*(?:\r\n?|\n)[ \t]*[A-Za-z0-9+/_=-]+/i;
const _INLINE_MEDIA_REDACTION = '[inline customer media removed]';
const _SHA256_METADATA_RE = /^sha256[_:][0-9a-f]{64}$/i;
const _SYNC_PROJECTION_OBJECT_KEY_RE = /^[0-9a-f-]{36}\/sync-v2\/[A-Za-z0-9._-]{1,128}\/generations\/[0-9a-f-]{36}\/members\/[0-9a-f]{64}\.json$/i;

const _KNOWN_MEDIA_CONTAINER_KEYS = new Set([
    'workerproxy', 'workerkeyframes', 'videoproxy', 'videokeyframes',
    'mediaproxy', 'proxy', 'keyframes', 'framedata',
]);
const _EXPLICIT_MEDIA_BASE64_KEYS = new Set([
    'imagebase64', 'videobase64', 'proxybase64', 'thumbnailbase64', 'posterbase64',
]);
const _GENERIC_BASE64_KEYS = new Set(['base64', 'database64']);
const _MEDIA_PAYLOAD_KEYS = new Set([
    'payload', 'raw', 'rawdata', 'binary', 'binarydata', 'encoded',
    'encodeddata', 'sourcebytes', 'originalbytes',
]);
const _BINARY_FIELD_KEYS = new Set([
    'bytes', 'buffer', 'arraybuffer', 'blob', 'file', 'docbytes', 'pdfblob',
]);

function _normaliseMediaKey(key) {
    return String(key ?? '').replace(/[_-]/g, '').toLowerCase();
}

function _isActualBinary(value) {
    if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
    return typeof ArrayBuffer !== 'undefined'
        && (value instanceof ArrayBuffer || ArrayBuffer.isView(value));
}

function _isByteArray(value) {
    return Array.isArray(value)
        && value.length > 0
        && value.every((entry) => Number.isInteger(entry) && entry >= 0 && entry <= 255);
}

function _readUint16LE(bytes, offset) {
    return bytes[offset] + (bytes[offset + 1] * 0x100);
}

function _readUint32LE(bytes, offset) {
    return bytes[offset]
        + (bytes[offset + 1] * 0x100)
        + (bytes[offset + 2] * 0x10000)
        + (bytes[offset + 3] * 0x1000000);
}

// `BM` alone is not a safe bitmap signature: ordinary Work Method prose such
// as `SUBMIT RECORDS` contains the same two bytes. Validate the complete BMP
// file/DIB header. Keep this byte-for-byte aligned with the backend policy.
function _hasPlausibleBmpHeaderAt(bytes, offset) {
    const available = bytes.length - offset;
    if (available < 26 || bytes[offset] !== 0x42 || bytes[offset + 1] !== 0x4d) return false;
    if (bytes[offset + 6] !== 0 || bytes[offset + 7] !== 0
        || bytes[offset + 8] !== 0 || bytes[offset + 9] !== 0) return false;

    const fileSize = _readUint32LE(bytes, offset + 2);
    const pixelOffset = _readUint32LE(bytes, offset + 10);
    const dibSize = _readUint32LE(bytes, offset + 14);
    if (fileSize > available || fileSize < 26) return false;
    if (dibSize < 12 || dibSize > fileSize - 14) return false;
    if (pixelOffset < 14 + dibSize || pixelOffset >= fileSize) return false;

    const planesOffset = offset + (dibSize === 12 ? 22 : 26);
    return planesOffset + 1 < bytes.length && _readUint16LE(bytes, planesOffset) === 1;
}

function _hasCustomerMediaSignatureAt(bytes, offset) {
    if (bytes.length - offset < 4) return false;
    const b0 = bytes[offset];
    const b1 = bytes[offset + 1];
    const b2 = bytes[offset + 2];
    const b3 = bytes[offset + 3];
    // JPEG, PNG, GIF, BMP and TIFF.
    if (b0 === 0xff && b1 === 0xd8 && b2 === 0xff) return true;
    if (bytes.length - offset >= 8
        && b0 === 0x89 && b1 === 0x50 && b2 === 0x4e && b3 === 0x47
        && bytes[offset + 4] === 0x0d && bytes[offset + 5] === 0x0a
        && bytes[offset + 6] === 0x1a && bytes[offset + 7] === 0x0a) return true;
    if (bytes.length - offset >= 6) {
        const prefix6 = String.fromCharCode(...bytes.slice(offset, offset + 6));
        if (prefix6 === 'GIF87a' || prefix6 === 'GIF89a') return true;
    }
    if (b0 === 0x42 && b1 === 0x4d && _hasPlausibleBmpHeaderAt(bytes, offset)) return true;
    if ((b0 === 0x49 && b1 === 0x49 && b2 === 0x2a && b3 === 0x00)
        || (b0 === 0x4d && b1 === 0x4d && b2 === 0x00 && b3 === 0x2a)) return true;

    // WebP / AVI, ISO-BMFF media, WebM/Matroska and MPEG streams.
    if (bytes.length - offset >= 12) {
        const prefix4 = String.fromCharCode(...bytes.slice(offset, offset + 4));
        const middle4 = String.fromCharCode(...bytes.slice(offset + 4, offset + 8));
        const container = String.fromCharCode(...bytes.slice(offset + 8, offset + 12));
        if (prefix4 === 'RIFF' && (container === 'WEBP' || container === 'AVI ')) return true;
        if (middle4 === 'ftyp') return true;
    }
    if (b0 === 0x1a && b1 === 0x45 && b2 === 0xdf && b3 === 0xa3) return true;
    return b0 === 0x00 && b1 === 0x00 && b2 === 0x01 && (b3 === 0xba || b3 === 0xb3);
}

function _containsCustomerMediaSignature(bytes) {
    if (!bytes || bytes.length < 4) return false;
    for (let offset = 0; offset <= bytes.length - 4; offset += 1) {
        if (_hasCustomerMediaSignatureAt(bytes, offset)) return true;
    }
    return false;
}

function _decodeFlexibleBase64(value) {
    const compact = String(value || '').replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/');
    if (compact.length < 16 || compact.length % 4 === 1 || !/^[A-Za-z0-9+/]*={0,2}$/.test(compact)) {
        return null;
    }
    const unpadded = compact.replace(/=+$/, '');
    const padded = unpadded + '='.repeat((4 - (unpadded.length % 4)) % 4);
    try {
        const decoded = atob(padded);
        if (decoded.length < 8) return null;
        const bytes = new Uint8Array(decoded.length);
        for (let index = 0; index < decoded.length; index += 1) bytes[index] = decoded.charCodeAt(index);
        return bytes;
    } catch {
        return null;
    }
}

function _containsRawCustomerMediaAcrossUnicode(value) {
    const text = String(value);
    let runStart = 0;
    for (let index = 0; index <= text.length; index += 1) {
        if (index < text.length && text.charCodeAt(index) <= 0xff) continue;
        if (index - runStart >= 4) {
            const bytes = new Uint8Array(index - runStart);
            for (let byteIndex = runStart; byteIndex < index; byteIndex += 1) {
                bytes[byteIndex - runStart] = text.charCodeAt(byteIndex);
            }
            if (_containsCustomerMediaSignature(bytes)) return true;
        }
        runStart = index + 1;
    }
    return false;
}

function _stringContainsEncodedCustomerMedia(value) {
    if (_MEDIA_DATA_URL_ANYWHERE_RE.test(String(value))) return true;
    const dataUrl = String(value).match(_GENERIC_DATA_URL_RE);
    if (dataUrl && /(?:^|;)base64(?:;|$)/i.test(dataUrl[1])) {
        const body = dataUrl[2].replace(/\s/g, '');
        if (body.length >= _MAX_OPAQUE_INLINE_STRING_CHARS) return true;
        const decoded = _decodeFlexibleBase64(body);
        if (decoded && _containsCustomerMediaSignature(decoded)) return true;
    }

    const compactWhole = String(value).replace(/\s/g, '');
    if (compactWhole.length >= _MAX_OPAQUE_INLINE_STRING_CHARS
        && /^[A-Za-z0-9+/_-]*={0,2}$/.test(compactWhole)) return true;
    const whole = _decodeFlexibleBase64(compactWhole);
    if (whole && _containsCustomerMediaSignature(whole)) return true;

    for (const match of String(value).matchAll(_BASE64_RUN_RE)) {
        if (match[0].length >= _MAX_OPAQUE_INLINE_STRING_CHARS) return true;
        const decoded = _decodeFlexibleBase64(match[0]);
        if (decoded && _containsCustomerMediaSignature(decoded)) return true;
    }
    for (const match of String(value).matchAll(_HEX_RUN_RE)) {
        if (match[0].length >= _MAX_OPAQUE_INLINE_STRING_CHARS * 2) return true;
        const bytes = new Uint8Array(match[0].length / 2);
        for (let index = 0; index < bytes.length; index += 1) {
            bytes[index] = Number.parseInt(match[0].slice(index * 2, index * 2 + 2), 16);
        }
        if (_containsCustomerMediaSignature(bytes)) return true;
    }

    // Unicode is a boundary between byte-sized runs, not a bypass for the
    // entire raw-media scan. The frontend deliberately mirrors the backend.
    if (_containsRawCustomerMediaAcrossUnicode(value)) return true;
    return false;
}

function _unsafeInlineMediaTextError(parentKey) {
    const err = new Error(
        `Project save blocked: inline customer media inside ${parentKey || 'project text'} `
        + 'could not be removed without risking loss of the surrounding user-authored text.',
    );
    err.code = 'INLINE_MEDIA_TEXT_REDACTION_UNSAFE';
    err.path = parentKey || null;
    err.retryable = true;
    return err;
}

/**
 * Remove encoded image/video TOKENS from prose without deleting the prose.
 * A whole-value media field still collapses to STRIPPED_CUSTOMER_MEDIA, while
 * notes/HTML/email text keeps its exact prefix, suffix and Unicode content.
 * If the guard can see media but cannot identify a bounded token, fail closed
 * rather than silently deleting the operator's complete field.
 */
function _stripCustomerMediaFromString(value, parentKey, mediaContext) {
    const key = _normaliseMediaKey(parentKey);
    if (_EXPLICIT_MEDIA_BASE64_KEYS.has(key)
        || (_GENERIC_BASE64_KEYS.has(key) && mediaContext)) {
        return value.trim().length > 0 ? STRIPPED_CUSTOMER_MEDIA : value;
    }
    if (mediaContext && _MEDIA_PAYLOAD_KEYS.has(key)) {
        const compact = value.replace(/\s/g, '');
        if (compact.length >= 32 && compact.length % 4 === 0
            && /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(compact)) {
            return STRIPPED_CUSTOMER_MEDIA;
        }
    }
    if (key.endsWith('hash') && _SHA256_METADATA_RE.test(value.trim())) return value;
    if (key === 'objectkey' && _SYNC_PROJECTION_OBJECT_KEY_RE.test(value.trim())) return value;
    if (_isBrowserBlobUrl(value) || _WHOLE_MEDIA_DATA_URL_RE.test(value)) {
        return STRIPPED_CUSTOMER_MEDIA;
    }

    let changed = false;
    let redacted = value.replace(
        _QUOTED_MEDIA_DATA_URL_RE,
        (_token, quote, leadingWhitespace) => {
            changed = true;
            return `${quote}${leadingWhitespace}${_INLINE_MEDIA_REDACTION}${quote}`;
        },
    );

    // Once a data URL folds across lines outside a quote, there is no reliable
    // boundary between payload continuation and operator prose. Preserve the
    // complete source field and fail closed instead of removing only the first
    // base64 line and leaking the rest.
    if (_FOLDED_UNQUOTED_MEDIA_DATA_URL_RE.test(redacted)) {
        throw _unsafeInlineMediaTextError(parentKey);
    }

    redacted = redacted.replace(
        _EMBEDDED_DATA_URL_TOKEN_RE,
        (token, header, body) => {
            const mediaMime = /^(?:image|video)\//i.test(String(header).trim());
            const base64Body = /(?:^|;)base64(?:;|$)/i.test(String(header));
            const decoded = base64Body ? _decodeFlexibleBase64(body) : null;
            if (!mediaMime
                && !(base64Body && (body.length >= _MAX_OPAQUE_INLINE_STRING_CHARS
                    || (decoded && _containsCustomerMediaSignature(decoded))))) {
                return token;
            }
            changed = true;
            return _INLINE_MEDIA_REDACTION;
        },
    );

    redacted = redacted.replace(new RegExp(_BASE64_RUN_RE.source, 'g'), (candidate) => {
        if (candidate.length < _MAX_OPAQUE_INLINE_STRING_CHARS) {
            const decoded = _decodeFlexibleBase64(candidate);
            if (!decoded || !_containsCustomerMediaSignature(decoded)) return candidate;
        }
        changed = true;
        return _INLINE_MEDIA_REDACTION;
    });
    redacted = redacted.replace(new RegExp(_HEX_RUN_RE.source, 'g'), (candidate) => {
        if (candidate.length < _MAX_OPAQUE_INLINE_STRING_CHARS * 2) {
            const bytes = new Uint8Array(candidate.length / 2);
            for (let index = 0; index < bytes.length; index += 1) {
                bytes[index] = Number.parseInt(candidate.slice(index * 2, index * 2 + 2), 16);
            }
            if (!_containsCustomerMediaSignature(bytes)) return candidate;
        }
        changed = true;
        return _INLINE_MEDIA_REDACTION;
    });

    if (changed) {
        if (_stringContainsEncodedCustomerMedia(redacted)) {
            throw _unsafeInlineMediaTextError(parentKey);
        }
        const authoredRemainder = redacted.split(_INLINE_MEDIA_REDACTION).join('').trim();
        return authoredRemainder ? redacted : STRIPPED_CUSTOMER_MEDIA;
    }
    if (_stringContainsEncodedCustomerMedia(value)) {
        throw _unsafeInlineMediaTextError(parentKey);
    }
    return value;
}

function _isSerialisedBuffer(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value)
        && value.type === 'Buffer' && _isByteArray(value.data);
}

function _isNumericByteObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const entries = Object.entries(value);
    return entries.length > 0 && entries.every(([key, entry]) => /^\d+$/.test(key)
        && Number.isInteger(entry) && entry >= 0 && entry <= 255);
}

function _objectDeclaresMediaMime(value) {
    if (!value || typeof value !== 'object') return false;
    return ['mimetype', 'mimeType', 'contentType', 'type'].some((key) => (
        typeof value[key] === 'string' && /^(?:image|video)\//i.test(value[key].trim())
    ));
}

/**
 * Final JSONB boundary guard. Project state has accumulated several nested
 * snapshots over time, so enumerating today's known photo/video lanes is not
 * sufficient: a future cache or copied Stage payload could otherwise carry an
 * inline customer image straight through to PostgreSQL.
 *
 * This deliberately matches the backend's customer-image/video byte policy.
 * Document data URLs are handled by the document migration rules above and
 * non-media values (for example extracted application payloads) are left
 * intact. The walk uses structural sharing so the live project object is never
 * mutated.
 */
function _stripNestedCustomerMediaDataUrls(
    value,
    seen = new WeakMap(),
    parentKey = null,
    mediaContext = false,
) {
    if (typeof value === 'string') {
        return _stripCustomerMediaFromString(value, parentKey, mediaContext);
    }
    if (_isActualBinary(value)) return STRIPPED_CUSTOMER_MEDIA;
    if (!value || typeof value !== 'object') return value;
    if (_isSerialisedBuffer(value)) return STRIPPED_CUSTOMER_MEDIA;

    const proto = Object.getPrototypeOf(value);
    const isArray = Array.isArray(value);
    const isPlainObject = proto === Object.prototype || proto === null;
    if (!isArray && !isPlainObject) return value;
    if (seen.has(value)) return seen.get(value);

    if (isArray) {
        const key = _normaliseMediaKey(parentKey);
        if (_isByteArray(value)) {
            if (value.length >= _MAX_OPAQUE_BYTE_ARRAY_LENGTH
                || _containsCustomerMediaSignature(Uint8Array.from(value))
                || _BINARY_FIELD_KEYS.has(key)
                || (mediaContext && _MEDIA_PAYLOAD_KEYS.has(key))) {
                return STRIPPED_CUSTOMER_MEDIA;
            }
        }
        let changed = false;
        const next = new Array(value.length);
        seen.set(value, next);
        for (let i = 0; i < value.length; i++) {
            const cleaned = _stripNestedCustomerMediaDataUrls(value[i], seen, null, mediaContext);
            if (cleaned === STRIPPED_CUSTOMER_MEDIA) {
                next[i] = undefined;
                changed = true;
            } else {
                next[i] = cleaned;
                if (cleaned !== value[i]) changed = true;
            }
        }
        if (!changed) {
            seen.set(value, value);
            return value;
        }
        return next;
    }

    const key = _normaliseMediaKey(parentKey);
    if ((_BINARY_FIELD_KEYS.has(key) || (mediaContext && _MEDIA_PAYLOAD_KEYS.has(key)))
        && _isNumericByteObject(value)) return STRIPPED_CUSTOMER_MEDIA;

    const childMediaContext = mediaContext
        || _KNOWN_MEDIA_CONTAINER_KEYS.has(key)
        || _objectDeclaresMediaMime(value);
    // Dexie blob ids are auto-increment integers scoped to one browser
    // profile. The same number can identify unrelated customer media in a
    // different browser, so these recovery locators never cross the Project
    // JSON wire boundary — even when no cloud object exists yet. The caller's
    // live/Dexie Project retains them and owner-validates them on local reopen.
    const deviceLocalKeysToStrip = new Set([
        'blobid',
        'docblobid',
        'videoblobid',
        'localblobonly',
    ]);
    let changed = false;
    const next = {};
    seen.set(value, next);
    for (const [key, child] of Object.entries(value)) {
        if (deviceLocalKeysToStrip.has(_normaliseMediaKey(key))) {
            changed = true;
            continue;
        }
        const cleaned = _stripNestedCustomerMediaDataUrls(
            child,
            seen,
            key,
            childMediaContext || _KNOWN_MEDIA_CONTAINER_KEYS.has(_normaliseMediaKey(key)),
        );
        if (cleaned === STRIPPED_CUSTOMER_MEDIA) {
            changed = true;
            continue;
        }
        next[key] = cleaned;
        if (cleaned !== child) changed = true;
    }
    if (!changed) {
        seen.set(value, value);
        return value;
    }
    return next;
}

function _stripPhotoInlineBytes(photo) {
    if (!photo || typeof photo !== 'object') return photo;
    // Customer media bytes never belong in the PostgreSQL project envelope,
    // including compressed frames. They remain in browser-local storage until
    // the protected derivative upload succeeds; the backend independently
    // rejects any inline image/video payload that evades this last-mile scrub.
    const next = { ...photo };
    let mutated = false;
    for (const key of ['data', 'dataUrl', 'originalData', 'aiMarkupData']) {
        const value = next[key];
        const binaryValue = (typeof Blob !== 'undefined' && value instanceof Blob)
            || (typeof ArrayBuffer !== 'undefined' && (value instanceof ArrayBuffer || ArrayBuffer.isView(value)));
        if (_isDataUrl(value) || _isBrowserBlobUrl(value) || binaryValue) {
            next[key] = undefined;
            mutated = true;
        }
    }

    const hasServerBacking = !!(photo.s3Key || photo.fileMetadataId || photo.cloudObjectKey);
    // `blobId` addresses one browser profile's global Dexie table. Persisting
    // it beside a portable S3/file-metadata receipt makes another browser treat
    // the same integer as its own local row, where it can belong to an entirely
    // different project. Keep the id in the caller's live object, but never in
    // the cloud projection once a server-backed source exists.
    if (hasServerBacking) {
        for (const key of ['blobId', '_localBlobOnly']) {
            if (next[key] !== undefined) {
                delete next[key];
                mutated = true;
            }
        }
    }
    if (!mutated) return photo;

    if (!hasServerBacking) {
        next._cloudUploadBlocked = true;
        next._cloudUploadBlockedReason = photo.cloudMedia?.mediaClass === CLOUD_MEDIA_CLASSES.LOCAL_ORIGINAL
            ? 'raw_original_cloud_upload_forbidden'
            : 'inline_customer_media_forbidden';
        if (photo.blobId) next._localBlobOnly = true;
        else next._localMemoryOnly = true;
    }
    return next;
}

function _stripDocumentInlineBytes(doc) {
    if (!doc || typeof doc !== 'object') return doc;
    // Every binary/browser-only source is local transport, not project JSON.
    // Uint8Array is especially dangerous here: JSON.stringify expands it into
    // an object with one numeric property per byte.  Deterministic intake moves
    // these bytes into document_blobs before save; this is the fail-safe that
    // guarantees they never cross the wire even when a caller misses migration.
    const transientKeys = new Set([
        'bytes', 'file', 'blob', 'handle', 'pdfBlob', 'pdfFile', 'docBytes',
        'arrayBuffer', 'fileHandle', 'directoryHandle',
    ]);
    let mutated = false;
    const next = {};
    for (const [key, value] of Object.entries(doc)) {
        const binaryValue = (typeof Blob !== 'undefined' && value instanceof Blob)
            || (typeof ArrayBuffer !== 'undefined' && (value instanceof ArrayBuffer || ArrayBuffer.isView(value)));
        if (transientKeys.has(key) || binaryValue) {
            mutated = true;
            continue;
        }
        if (key === 'data' && _isDataUrl(value)) {
            mutated = true;
            continue;
        }
        next[key] = value;
    }
    // Browser preview URLs and base64 document payloads are never part of the
    // backend project envelope. Callers must migrate them first; retaining an
    // unbacked data URL here would silently turn a missed migration into a huge
    // JSONB save. Deterministic intake fails closed when durable backing could
    // not be created.
    if ((typeof next.pdfDataUrl === 'string' && next.pdfDataUrl.startsWith('blob:'))
        || _isDataUrl(next.pdfDataUrl)) {
        delete next.pdfDataUrl; mutated = true;
    }
    if (_documentHasCloudBacking(doc) && next.docBlobId !== undefined) {
        delete next.docBlobId;
        mutated = true;
    }
    return mutated ? next : doc;
}

function _stripVideoTransientBytes(video) {
    if (!video || typeof video !== 'object') return video;
    const transientKeys = new Set([
        'file', 'blob', 'bytes', 'handle', 'fileHandle', 'directoryHandle',
        // Server-transcoded proxies/keyframes are persisted through protected
        // object lanes, never as nested base64 inside the project JSONB row.
        'workerProxy', 'workerKeyframes',
    ]);
    let mutated = false;
    const next = {};
    for (const [key, value] of Object.entries(video)) {
        const binaryValue = (typeof Blob !== 'undefined' && value instanceof Blob)
            || (typeof ArrayBuffer !== 'undefined' && (value instanceof ArrayBuffer || ArrayBuffer.isView(value)));
        if (transientKeys.has(key) || binaryValue) {
            mutated = true;
            continue;
        }
        if ((key === 'data' || key === 'dataUrl') && _isDataUrl(value)) {
            mutated = true;
            continue;
        }
        if (key === 'thumbnail' && _isDataUrl(value)) {
            mutated = true;
            continue;
        }
        if (key === 'url' && typeof value === 'string' && value.startsWith('blob:')) {
            mutated = true;
            continue;
        }
        next[key] = value;
    }
    if (_videoHasCloudBacking(video) && next.videoBlobId !== undefined) {
        delete next.videoBlobId;
        mutated = true;
    }
    return mutated ? next : video;
}

function _walkScopeForInlinePhotos(scope) {
    if (!Array.isArray(scope)) return scope;
    let changed = false;
    const out = scope.map((item) => {
        if (!item || typeof item !== 'object') return item;
        if (!Array.isArray(item.images) || item.images.length === 0) return item;
        let imagesChanged = false;
        const stripped = item.images.map((img) => {
            const next = _stripPhotoInlineBytes(img);
            if (next !== img) imagesChanged = true;
            return next;
        });
        if (!imagesChanged) return item;
        changed = true;
        return { ...item, images: stripped };
    });
    return changed ? out : scope;
}

function _projectMediaDurabilityError(path, cause = null) {
    const err = new Error(
        `Project save blocked: ${path} still contains customer photo/video bytes without `
        + 'a durable browser or cloud reference. The media remains on this screen; retry the save after storage recovers.',
    );
    err.code = 'MEDIA_DURABILITY_REQUIRED';
    err.path = path;
    err.retryable = true;
    if (cause) err.cause = cause;
    return err;
}

function _boundedCauseDetails(cause) {
    if (!cause || typeof cause !== 'object') return null;
    const code = cause.code ?? cause.errorCode ?? cause.body?.code ?? null;
    const status = Number(cause.status ?? cause.statusCode ?? 0) || null;
    const message = typeof cause.message === 'string'
        // No URLs: a presigned upload URL carries credentials in its query.
        ? cause.message.replace(/https?:\/\/\S+/g, '<url>').slice(0, 300)
        : null;
    // The file service names what storage objected to (see storageFailureReason
    // in backend/src/routes/files.js). Without this the job record could only
    // ever say "Retry shortly", which nobody can act on.
    const served = cause.body?.reason ?? cause.reason ?? null;
    const reason = typeof served === 'string' && served.trim() ? served.trim().slice(0, 300) : null;
    if (!code && !status && !message && !reason) return null;
    return {
        causeCode: code ? String(code) : null,
        causeStatus: status,
        causeMessage: message,
        ...(reason ? { reason } : {}),
    };
}

function _projectDocumentDurabilityError(path, document = null, cause = null) {
    const name = String(document?.name || document?.originalName || '').trim();
    const err = new Error(
        `Project save blocked: reference document${name ? ` "${name}"` : ''} could not be durably uploaded `
        + `before cloud JSON sanitisation (${path}). Its local bytes remain in the open project. `
        + 'Keep QuoteMaker open, restore the login/file service if needed, then retry the save.',
    );
    err.code = 'DOCUMENT_DURABILITY_REQUIRED';
    err.category = 'project-persistence';
    err.path = path;
    err.retryable = true;
    err.operatorAction = 'Keep QuoteMaker open, restore the login or file service if needed, then retry the save.';
    if (cause) {
        err.cause = cause;
        // err.cause does not survive onto the queue's job record, so the real
        // reason (a 400, a 413, a refused type) was invisible to the operator
        // and to anyone reading the record afterwards. Bounded and URL-free.
        const details = _boundedCauseDetails(cause);
        if (details) err.details = { ...(err.details || {}), ...details };
        // The file service refused this file (its type, its bytes, its size).
        // The same file is refused on every retry, so the stop is final and
        // the advice is not to restore a service that answered (2026-10-04
        // UAT F2: the job said "Reconnect QuoteMaker" for a refused .md).
        if (_DOCUMENT_REFUSAL_STATUSES.has(Number(details?.causeStatus))) {
            err.status = Number(details.causeStatus);
            err.retryable = false;
            err.operatorAction = 'The file service refused this file, so saving again will not store it. '
                + 'Leave the file out, or save it as a PDF or Word file and add that instead.';
        }
    }
    // The job record names the file it could not store (bounded; a file name only).
    if (name) err.details = { ...(err.details || {}), documentName: name.slice(0, 200) };
    return err;
}

// Answers that refuse the file itself: the same file is refused every time.
const _DOCUMENT_REFUSAL_STATUSES = new Set([400, 413, 415, 422]);

function _photoHasInlineBytes(photo) {
    if (!photo || typeof photo !== 'object') return false;
    return ['data', 'dataUrl', 'originalData', 'aiMarkupData'].some((key) => (
        _isDataUrl(photo[key]) || _isBrowserBlobUrl(photo[key]) || _isActualBinary(photo[key])
    ));
}

function _photoHasDurableBacking(photo, durablePhotoIds = null) {
    if (!photo || typeof photo !== 'object') return false;
    if (photo.s3Key || photo.fileMetadataId || photo.cloudObjectKey || photo.blobId) return true;
    return !!(photo.id != null && durablePhotoIds?.has(String(photo.id)));
}

function _videoHasInlineBytes(video) {
    if (!video || typeof video !== 'object') return false;
    if (['file', 'blob', 'bytes', 'data', 'dataUrl'].some((key) => (
        _isActualBinary(video[key]) || _isDataUrl(video[key]) || _isByteArray(video[key])
        || _isSerialisedBuffer(video[key])
    ))) return true;
    if (typeof video.url === 'string' && video.url.startsWith('blob:')) return true;
    if (_isDataUrl(video.thumbnail)) return true;
    return !!(video.workerProxy || video.workerKeyframes);
}

function _videoHasDurableBacking(video) {
    return !!(video && typeof video === 'object' && (
        video.videoBlobId
        || video.s3Key
        || video.fileMetadataId
        || video.cloudPlayback?.s3Key
        || video.cloudPlayback?.fileMetadataId
    ));
}

function _videoHasCloudBacking(video) {
    return !!(video && typeof video === 'object' && (
        video.s3Key
        || video.fileMetadataId
        || video.cloudPlayback?.s3Key
        || video.cloudPlayback?.fileMetadataId
    ));
}

function _dataUrlToBlobForPersistence(value) {
    if (!_isDataUrl(value)) return null;
    const comma = value.indexOf(',');
    if (comma < 0) return null;
    const header = value.slice(5, comma);
    const mimeType = header.split(';')[0] || 'application/octet-stream';
    try {
        const base64 = value.slice(comma + 1).replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.replace(/=+$/, '') + '='.repeat((4 - (base64.replace(/=+$/, '').length % 4)) % 4);
        const decoded = atob(padded);
        const bytes = new Uint8Array(decoded.length);
        for (let index = 0; index < decoded.length; index += 1) bytes[index] = decoded.charCodeAt(index);
        return new Blob([bytes], { type: mimeType });
    } catch {
        return null;
    }
}

function _binaryValueToBlob(value, contentType = 'application/octet-stream') {
    if (typeof Blob !== 'undefined' && value instanceof Blob) return value;
    if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
        return new Blob([value], { type: contentType });
    }
    if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(value)) {
        return new Blob([value], { type: contentType });
    }
    if (_isByteArray(value)) return new Blob([Uint8Array.from(value)], { type: contentType });
    if (_isSerialisedBuffer(value)) return new Blob([Uint8Array.from(value.data)], { type: contentType });
    if (_isNumericByteObject(value)) {
        const bytes = Object.entries(value)
            .sort(([left], [right]) => Number(left) - Number(right))
            .map(([, byte]) => byte);
        return new Blob([Uint8Array.from(bytes)], { type: contentType });
    }
    if (_isDataUrl(value)) return _dataUrlToBlobForPersistence(value);
    return null;
}

const _DOCUMENT_MIME_BY_EXTENSION = Object.freeze({
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    txt: 'text/plain',
    // Markdown and .text notes are plain text. The Quote Run scan accepts them
    // (file-explorer-intake.ts TEXT_EXTENSIONS) and the server's
    // project_document lane registers text/plain, not text/markdown, and
    // checks text/plain bytes with its control-byte rule. 2026-10-04 UAT F1:
    // an "Inspection notes.md" went out as text/markdown or
    // application/octet-stream, was refused, and stopped the whole job.
    md: 'text/plain',
    markdown: 'text/plain',
    text: 'text/plain',
    csv: 'text/csv',
    msg: 'application/vnd.ms-outlook',
    eml: 'message/rfc822',
});

// What browsers report for a Markdown file. The server registers it as text/plain.
const _MARKDOWN_MIMES = new Set(['text/markdown', 'text/x-markdown']);

function _documentFileName(document, index = 0) {
    return String(document?.name || document?.originalName || document?.fileName || `document-${index + 1}`);
}

function _documentExtension(document, index = 0) {
    const explicit = String(document?.ext || '').trim().replace(/^\./, '').toLowerCase();
    if (explicit) return explicit;
    const name = _documentFileName(document, index);
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

function _normaliseDocumentMimeCandidate(value) {
    const candidate = String(value || '').trim().toLowerCase().split(';', 1)[0].trim();
    return /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/i.test(candidate)
        ? candidate
        : '';
}

function _documentContentType(document, index = 0) {
    // `document.type` is also QuoteMaker's semantic classification lane
    // (`rfq`, `report`, `correspondence`, ...). It is only a MIME fallback
    // when it is actually MIME-shaped. Treating `type: "report"` as a content
    // type makes the protected project_document upload policy reject an
    // otherwise-valid PDF before the project can be saved.
    const explicit = _normaliseDocumentMimeCandidate(document?.contentType)
        || _normaliseDocumentMimeCandidate(document?.mimeType);
    if (_MARKDOWN_MIMES.has(explicit)) return 'text/plain';
    if (explicit && explicit !== 'application/octet-stream') return explicit;

    const fromExtension = _DOCUMENT_MIME_BY_EXTENSION[_documentExtension(document, index)];
    if (fromExtension) return fromExtension;

    const legacyType = _normaliseDocumentMimeCandidate(document?.type);
    if (_MARKDOWN_MIMES.has(legacyType)) return 'text/plain';
    return (legacyType && legacyType !== 'application/octet-stream')
        ? legacyType
        : explicit || legacyType || 'application/octet-stream';
}

function _isPdfDocument(document, index = 0) {
    return _documentExtension(document, index) === 'pdf'
        || _documentContentType(document, index) === 'application/pdf';
}

function _imageMimeOf(value) {
    const mime = _normaliseDocumentMimeCandidate(value);
    return mime.startsWith('image/') ? mime : '';
}

// Stage 1 keeps a picture that carries document text as a documents[] row: the
// original in `data`, the text read from it in `content`, `isImageDoc` true, and
// no MIME. The server stores a customer image only as a compressed derivative
// with a receipt, never on the document lane, so such a row is saved through the
// photo derivative path. Contents decide, never the file name: the row's
// declared type, the type its data URL or stored bytes carry, or its flag.
function _isImageDocument(document, source) {
    if (document?.isImageDoc === true) return true;
    const dataUrlType = typeof document?.data === 'string' ? /^data:([^;,]*)/i.exec(document.data)?.[1] : '';
    return !!(_imageMimeOf(document?.contentType) || _imageMimeOf(document?.mimeType)
        || _imageMimeOf(dataUrlType) || _imageMimeOf(source?.type));
}

/**
 * Store an image document's compressed derivative on the protected photo lane,
 * with the receipt the lane requires, and return the row with its durable
 * reference. The row keeps its original, text and source identity: only the
 * cloud reference is added, and the cloud scrub removes the inline original.
 */
async function _uploadImageDocumentDerivative(document, source, projectId, index, deps) {
    const prepareImageDerivative = deps.prepareImageDerivative
        || (await import('./media-processor')).compressImage;
    const name = _documentFileName(document, index);
    const prepared = await prepareImageDerivative(new File([source], name, { type: _imageMimeOf(source.type) }));
    // Only a complete compressed-derivative receipt may leave the browser: a
    // local-original, missing or incomplete one blocks the save with the
    // original still on the row.
    const decision = decideCloudMediaUpload(prepared?.cloudMedia, expectedCloudMediaKindForEntityType('project_photo'));
    if (!(prepared?.blob instanceof Blob) || prepared.blob.size === 0 || !decision.allowed) {
        throw new Error(`The picture could not be prepared for cloud storage (${decision.reason}).`);
    }
    // The upload service refuses a content type that differs from the receipt,
    // so the receipt's own type wins.
    const contentType = prepared.cloudMedia.derivativeContentType;
    const stem = name.replace(/\.[^./\\]+$/, '') || `document-${index + 1}`;
    const result = await deps.uploadFileToS3({
        data: prepared.blob,
        projectId,
        entityType: 'project_photo',
        entityId: document.id || document.fileId || `document-${index + 1}`,
        contentType,
        originalFilename: `${stem}.${contentType === 'image/webp' ? 'webp' : 'jpg'}`,
        cloudMedia: prepared.cloudMedia,
    });
    if (!result?.s3Key && !result?.fileMetadataId) {
        throw new Error('The file service returned no durable object or metadata reference.');
    }
    // The receipt kept on the row is the one the SERVER issued for the stored
    // derivative, not the browser's claim about it.
    if (!result.cloudMedia || typeof result.cloudMedia !== 'object') {
        throw new Error('The file service returned no derivative receipt.');
    }
    return {
        ...document,
        ...(result.s3Key ? { s3Key: result.s3Key } : {}),
        ...(result.fileMetadataId ? { fileMetadataId: result.fileMetadataId } : {}),
        contentType: result.contentType || contentType,
        cloudMedia: result.cloudMedia,
        pdfPreviewUnavailableReason: undefined,
    };
}

function _documentHasCloudBacking(document) {
    return !!(document && typeof document === 'object' && (
        document.fileMetadataId
        || document.pdfS3Key
        || document.s3Key
        || document.fileS3Key
        || document.cloudObjectKey
    ));
}

function _documentHasTransientBytes(document) {
    if (!document || typeof document !== 'object') return false;
    for (const key of [
        'bytes', 'file', 'blob', 'pdfBlob', 'pdfFile', 'docBytes', 'arrayBuffer',
        'data', 'pdfDataUrl', 'handle', 'fileHandle',
    ]) {
        const value = document[key];
        if (value == null) continue;
        if (key === 'handle' || key === 'fileHandle') {
            if (typeof value?.getFile === 'function') return true;
            continue;
        }
        if ((key === 'data' || key === 'pdfDataUrl')
            && typeof value === 'string'
            && (_isDataUrl(value) || value.startsWith('blob:'))) return true;
        // These named fields are removed unconditionally by the cloud JSON
        // scrub. Treat any non-null value as source-bearing so an unfamiliar
        // File-like representation is rejected instead of silently discarded.
        if (!['data', 'pdfDataUrl'].includes(key)) return true;
        if (_binaryValueToBlob(value, _documentContentType(document))) return true;
    }
    return false;
}

async function _documentSourceBlob(document, projectId, index, deps = {}) {
    const contentType = _documentContentType(document, index);
    // EVERY branch below must hand back bytes typed the way pre-registration
    // recorded them. Documents over 20 MB go out as multipart, where the server
    // compares the part's own Content-Type against the upload record; smaller
    // ones take the JSON path, which sends the resolved type as a field and so
    // always agreed.
    //
    // Live, 2026-09-20: a 28.6 MB .eml stored in IndexedDB as
    // application/octet-stream was refused with "Multipart contentType does not
    // match the upload record" while message/rfc822 was registered. Fixing only
    // the in-memory branch was not enough: a document loaded from a saved
    // project has no live File, so it resolves through the docBlobId branch at
    // the bottom, which returned the stored blob untouched.
    const asRegisteredType = (source) => {
        if (!source || source.type === contentType) return source;
        // application/octet-stream here only means no type was ever recorded for
        // the row. A picture's bytes already carry their real type, and erasing
        // it hid the picture from the lane it belongs on.
        if (contentType === 'application/octet-stream' && _imageMimeOf(source.type)) return source;
        return source.slice(0, source.size, contentType);
    };
    const resolve = async () => {
        for (const value of [
            document?.file,
            document?.blob,
            document?.pdfBlob,
            document?.pdfFile,
            document?.bytes,
            document?.docBytes,
            document?.arrayBuffer,
            document?.data,
            document?.pdfDataUrl,
        ]) {
            const source = _binaryValueToBlob(value, contentType);
            if (source) return source;
        }

        for (const value of [document?.file, document?.blob, document?.pdfBlob, document?.pdfFile]) {
            if (typeof value?.arrayBuffer !== 'function') continue;
            const bytes = await value.arrayBuffer();
            const source = _binaryValueToBlob(bytes, value.type || contentType);
            if (source) return source;
        }

        for (const handle of [document?.fileHandle, document?.handle]) {
            if (typeof handle?.getFile !== 'function') continue;
            const file = await handle.getFile();
            const source = _binaryValueToBlob(file, contentType);
            if (source) return source;
        }

        if (typeof document?.pdfDataUrl === 'string' && document.pdfDataUrl.startsWith('blob:')) {
            const fetchDocumentSource = deps.fetchDocumentSource || globalThis.fetch;
            if (typeof fetchDocumentSource !== 'function') {
                throw new Error('The browser could not read the live PDF preview source.');
            }
            const response = await fetchDocumentSource(document.pdfDataUrl);
            if (!response?.ok) throw new Error(`The live PDF preview source could not be read (${response?.status || 'network error'}).`);
            const blob = await response.blob();
            if (!blob || blob.size <= 0) throw new Error('The live PDF preview source was empty.');
            return blob;
        }

        if (document?.docBlobId) {
            const getDocumentBlob = deps.getDocumentBlob
                || (await import('./document-blob-store.ts')).getDocumentBlob;
            return await getDocumentBlob(document.docBlobId, projectId);
        }
        return null;
    };
    return asRegisteredType(await resolve());
}

function _assertCanonicalDocumentLaneDurable(project) {
    const documents = Array.isArray(project?.documents) ? project.documents : [];
    for (let index = 0; index < documents.length; index += 1) {
        const document = documents[index];
        if (!document || typeof document !== 'object') continue;
        const uploadPreviouslyFailed = document._cloudUploadBlockedReason === 'document_upload_failed';
        if ((uploadPreviouslyFailed || _documentHasTransientBytes(document))
            && !_documentHasCloudBacking(document)) {
            throw _projectDocumentDurabilityError(`documents[${index}]`, document);
        }
    }
}

function _videoSourceBlob(video) {
    const contentType = video?.contentType || video?.mimeType || video?.type || 'video/mp4';
    for (const value of [video?.file, video?.blob, video?.data, video?.dataUrl, video?.bytes]) {
        const source = _binaryValueToBlob(value, contentType);
        if (source) return source;
    }
    const proxy = video?.workerProxy;
    if (proxy && typeof proxy === 'object') {
        const proxySource = _binaryValueToBlob(
            proxy.blob || proxy.bytes || (proxy.base64 ? `data:${proxy.mimeType || proxy.contentType || contentType};base64,${proxy.base64}` : null),
            proxy.mimeType || proxy.contentType || contentType,
        );
        if (proxySource) return proxySource;
    }
    return null;
}

function _assertCanonicalPhotoLanesDurable(project) {
    const photos = Array.isArray(project?.photos) ? project.photos : [];
    const durablePhotoIds = new Set(photos
        .filter((photo) => _photoHasDurableBacking(photo) && photo.id != null)
        .map((photo) => String(photo.id)));
    const lanes = [
        ['photos', photos],
        ['scope', Array.isArray(project?.scope)
            ? project.scope.flatMap((item) => Array.isArray(item?.images) ? item.images : [])
            : []],
        ['quoteData.scope', Array.isArray(project?.quoteData?.scope)
            ? project.quoteData.scope.flatMap((item) => Array.isArray(item?.images) ? item.images : [])
            : []],
    ];
    for (const [lane, values] of lanes) {
        for (let index = 0; index < values.length; index += 1) {
            const photo = values[index];
            if (_photoHasInlineBytes(photo) && !_photoHasDurableBacking(photo, durablePhotoIds)) {
                throw _projectMediaDurabilityError(`${lane}[${index}]`);
            }
        }
    }
}

function _assertCanonicalVideoLaneDurable(project) {
    const videos = Array.isArray(project?.videos) ? project.videos : [];
    for (let index = 0; index < videos.length; index += 1) {
        if (_videoHasInlineBytes(videos[index]) && !_videoHasDurableBacking(videos[index])) {
            throw _projectMediaDurabilityError(`videos[${index}]`);
        }
    }
}

/**
 * EVID: an estimator-notes row carries the notes as written (rawNotesBlob, or
 * rawNotesBlobId once the local save stored it; stage1-estimator-notes.ts).
 * They go to the brain-only lane `project_builder_notes`, which no download
 * route serves, and the row keeps only the key (rawNotesS3Key). Notes held
 * only in memory must reach the server or the save stops, like any document's
 * bytes; notes with a local copy that cannot upload now wait for the next save.
 */
async function _uploadRawEstimatorNotes(project, projectId, deps = {}) {
    const documents = Array.isArray(project?.documents) ? project.documents : [];
    const pending = (d) => d && typeof d === 'object' && !d.rawNotesS3Key
        && ((typeof Blob !== 'undefined' && d.rawNotesBlob instanceof Blob) || d.rawNotesBlobId);
    if (!documents.some(pending)) return project;
    const out = [];
    let changed = false;
    for (let index = 0; index < documents.length; index += 1) {
        const document = documents[index];
        if (!pending(document)) { out.push(document); continue; }
        const inMemory = typeof Blob !== 'undefined' && document.rawNotesBlob instanceof Blob;
        try {
            if (!projectId) throw new Error('The project has no id yet.');
            let source = inMemory ? document.rawNotesBlob : null;
            if (!source) {
                const getDocumentBlob = deps.getDocumentBlob
                    || (await import('./document-blob-store.ts')).getDocumentBlob;
                source = await getDocumentBlob(document.rawNotesBlobId, projectId);
                // A browser-local id names a row in THIS browser. On another
                // device the same number can be a different file, so only the
                // text this lane stored is accepted.
                if (source && source.type !== 'text/plain') source = null;
            }
            if (!source) throw new Error('The notes are not stored in this browser.');
            const uploadFileToS3 = deps.uploadFileToS3
                || (await import('./file-upload-service')).uploadFileToS3;
            const result = await uploadFileToS3({
                data: source.type === 'text/plain' ? source : source.slice(0, source.size, 'text/plain'),
                projectId,
                entityType: 'project_builder_notes',
                entityId: document.id || `document-${index + 1}`,
                contentType: 'text/plain',
                originalFilename: _documentFileName(document, index),
            });
            if (!result?.s3Key) throw new Error('The file service returned no object key.');
            const { rawNotesBlob: _rawNotesBlob, ...rest } = document;
            out.push({
                ...rest,
                rawNotesS3Key: result.s3Key,
                ...(result.fileMetadataId ? { rawNotesFileMetadataId: result.fileMetadataId } : {}),
            });
            changed = true;
        } catch (error) {
            if (inMemory && !document.rawNotesBlobId) {
                throw _projectDocumentDurabilityError(`documents[${index}]`, document, error);
            }
            console.warn(`[DataService] Estimator notes "${_documentFileName(document, index)}" stay in this browser until the next save: ${error?.message || error}`);
            out.push(document);
        }
    }
    return changed ? { ...project, documents: out } : project;
}

/**
 * The cloud scrub is allowed to remove bytes only after an equivalent local or
 * cloud source exists. This is deliberately ahead of JSON sanitisation and is
 * shared by every direct projects.save caller, not only the mounted editor.
 */
async function _ensureProjectMediaDurableForSave(project, deps = {}) {
    if (!project || typeof project !== 'object') return project;
    const projectId = String(project.id || project.dexieId || '').trim();
    let next = project;

    const documents = Array.isArray(project.documents) ? project.documents : [];
    if (documents.length > 0) {
        const durableDocuments = [];
        let documentsChanged = false;
        for (let index = 0; index < documents.length; index += 1) {
            const document = documents[index];
            if (!document || typeof document !== 'object' || _documentHasCloudBacking(document)) {
                durableDocuments.push(document);
                continue;
            }

            const hasTransientBytes = _documentHasTransientBytes(document);
            let source = null;
            try {
                if (hasTransientBytes || document.docBlobId) {
                    source = await _documentSourceBlob(document, projectId, index, deps);
                }
            } catch (error) {
                if (hasTransientBytes) {
                    throw _projectDocumentDurabilityError(`documents[${index}]`, document, error);
                }
            }

            // A historical cloud row can carry a device-local docBlobId whose
            // IndexedDB row is unavailable on this browser. Saving the unchanged
            // metadata does not discard bytes from the live project, so leave it
            // alone. Any source we can actually resolve is promoted to cloud.
            if (!source) {
                if (hasTransientBytes) {
                    throw _projectDocumentDurabilityError(`documents[${index}]`, document);
                }
                durableDocuments.push(document);
                continue;
            }
            if (!projectId) {
                throw _projectDocumentDurabilityError(`documents[${index}]`, document);
            }

            try {
                const uploadFileToS3 = deps.uploadFileToS3
                    || (await import('./file-upload-service')).uploadFileToS3;
                if (_isImageDocument(document, source)) {
                    durableDocuments.push(await _uploadImageDocumentDerivative(
                        document,
                        source,
                        projectId,
                        index,
                        { uploadFileToS3, prepareImageDerivative: deps.prepareImageDerivative },
                    ));
                    documentsChanged = true;
                    continue;
                }
                const contentType = _documentContentType(document, index);
                const result = await uploadFileToS3({
                    data: source,
                    projectId,
                    entityType: 'project_document',
                    entityId: document.id || document.fileId || `document-${index + 1}`,
                    contentType,
                    originalFilename: _documentFileName(document, index),
                });
                if (!result?.s3Key && !result?.fileMetadataId) {
                    throw new Error('The file service returned no durable object or metadata reference.');
                }
                const uploaded = {
                    ...document,
                    ...(result.s3Key
                        ? (_isPdfDocument(document, index)
                            ? { pdfS3Key: result.s3Key }
                            : { s3Key: result.s3Key })
                        : {}),
                    ...(result.fileMetadataId ? { fileMetadataId: result.fileMetadataId } : {}),
                    contentType: result.contentType || contentType,
                    pdfPreviewUnavailableReason: undefined,
                };
                durableDocuments.push(uploaded);
                documentsChanged = true;
            } catch (error) {
                throw _projectDocumentDurabilityError(`documents[${index}]`, document, error);
            }
        }
        if (documentsChanged) next = { ...next, documents: durableDocuments };
    }
    next = await _uploadRawEstimatorNotes(next, projectId, deps);

    const photos = Array.isArray(next.photos) ? next.photos : [];
    // Even a claimed blobId must be verified by migrateProjectPhotos before
    // inline bytes are discarded; stale/foreign IndexedDB ids are not durable.
    if (photos.some((photo) => _photoHasInlineBytes(photo)
        && !(photo?.s3Key || photo?.fileMetadataId || photo?.cloudObjectKey))) {
        if (!projectId) throw _projectMediaDurabilityError('photos');
        try {
            const migrateProjectPhotos = deps.migrateProjectPhotos
                || (await import('./photo-blob-store')).migrateProjectPhotos;
            const migrated = await migrateProjectPhotos(projectId, photos);
            if (Array.isArray(migrated?.photos)) next = { ...next, photos: migrated.photos };
        } catch (error) {
            throw _projectMediaDurabilityError('photos', error);
        }
    }
    _assertCanonicalPhotoLanesDurable(next);

    const videos = Array.isArray(next.videos) ? next.videos : [];
    if (videos.some((video) => _videoHasInlineBytes(video) && !_videoHasCloudBacking(video))) {
        if (!projectId) throw _projectMediaDurabilityError('videos');
        const storeVideoBlob = deps.storeVideoBlob
            || (await import('./video-blob-store')).storeVideoBlob;
        const durableVideos = [];
        for (let index = 0; index < videos.length; index += 1) {
            const video = videos[index];
            if (!_videoHasInlineBytes(video) || _videoHasCloudBacking(video)) {
                durableVideos.push(video);
                continue;
            }
            if (video.videoBlobId) {
                try {
                    const videoBlobStore = await import('./video-blob-store');
                    const getVideoBlob = deps.getVideoBlob || videoBlobStore.getVideoBlob;
                    const existingBlob = await getVideoBlob(video.videoBlobId, projectId);
                    if (existingBlob) {
                        durableVideos.push(video);
                        continue;
                    }
                } catch { /* fall through and replace the unverified blob ref */ }
            }
            const source = _videoSourceBlob(video);
            if (!source) throw _projectMediaDurabilityError(`videos[${index}]`);
            try {
                const videoBlobId = await storeVideoBlob(source, projectId, {
                    name: video.name || video.originalName || `video-${index + 1}`,
                    type: source.type || video.contentType || video.mimeType || 'video/mp4',
                    size: source.size,
                });
                if (!videoBlobId) throw new Error('Video blob store returned no durable id.');
                durableVideos.push({ ...video, videoBlobId });
            } catch (error) {
                throw _projectMediaDurabilityError(`videos[${index}]`, error);
            }
        }
        next = { ...next, videos: durableVideos };
    }
    _assertCanonicalVideoLaneDurable(next);
    return next;
}

function _byteSize(s) {
    if (typeof s !== 'string') return 0;
    // UTF-16 BMP heuristic: ascii ≈ 1 byte/char, mid-range up to 3. For our
    // payloads (base64 + JSON syntax) ~= 1 byte/char is close enough.
    return s.length;
}

function _logProjectPayloadBreakdown(payload, totalBytes) {
    try {
        const fieldSizes = [];
        for (const [k, v] of Object.entries(payload)) {
            if (v == null) continue;
            const bytes = (typeof v === 'string') ? _byteSize(v) : _byteSize(JSON.stringify(v));
            if (bytes >= 64 * 1024) fieldSizes.push([k, bytes]);
        }
        fieldSizes.sort((a, b) => b[1] - a[1]);
        const fmt = (n) => (n >= 1024 * 1024)
            ? `${(n / 1024 / 1024).toFixed(2)} MB`
            : `${(n / 1024).toFixed(1)} KB`;
        const rows = fieldSizes.slice(0, 10).map(([k, b]) => `  ${k}: ${fmt(b)}`).join('\n');
        console.warn(
            `[projects.save] Large payload (${fmt(totalBytes)}) — top fields:\n${rows}`,
        );
    } catch (e) {
        console.warn('[projects.save] payload-size breakdown failed:', e?.message);
    }
}

// Compact "field: size" summary of the largest top-level payload fields, for a
// user-facing error message (the full breakdown still goes to the console via
// _logProjectPayloadBreakdown). Returns e.g. "photos: 45.2 MB, documents: 8.1 MB".
function _topPayloadFields(payload, n = 3) {
    try {
        const sizes = [];
        for (const [k, v] of Object.entries(payload || {})) {
            if (v == null) continue;
            const bytes = (typeof v === 'string') ? _byteSize(v) : _byteSize(JSON.stringify(v));
            if (bytes >= 256 * 1024) sizes.push([k, bytes]);
        }
        sizes.sort((a, b) => b[1] - a[1]);
        const fmt = (b) => (b >= 1024 * 1024) ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;
        return sizes.slice(0, n).map(([k, b]) => `${k} ${fmt(b)}`).join(', ');
    } catch {
        return '';
    }
}

function _stripInlineMediaForSave(project) {
    if (!project || typeof project !== 'object') return project;
    let mutated = false;
    let next = project;

    if (Array.isArray(project.photos)) {
        let photosChanged = false;
        const photos = project.photos.map((p) => {
            const stripped = _stripPhotoInlineBytes(p);
            if (stripped !== p) photosChanged = true;
            return stripped;
        });
        if (photosChanged) { next = { ...next, photos }; mutated = true; }
    }

    if (Array.isArray(project.documents)) {
        let docsChanged = false;
        const documents = project.documents.map((d) => {
            const stripped = _stripDocumentInlineBytes(d);
            if (stripped !== d) docsChanged = true;
            return stripped;
        });
        if (docsChanged) { next = { ...next, documents }; mutated = true; }
    }

    if (Array.isArray(project.videos)) {
        let videosChanged = false;
        const videos = project.videos.map((video) => {
            const stripped = _stripVideoTransientBytes(video);
            if (stripped !== video) videosChanged = true;
            return stripped;
        });
        if (videosChanged) { next = { ...next, videos }; mutated = true; }
    }

    if (Array.isArray(project.scope)) {
        const scope = _walkScopeForInlinePhotos(project.scope);
        if (scope !== project.scope) { next = { ...next, scope }; mutated = true; }
    }

    if (project.selectedHeroImage && typeof project.selectedHeroImage === 'object'
        && !Array.isArray(project.selectedHeroImage)) {
        const selectedHeroImage = _stripPhotoInlineBytes(project.selectedHeroImage);
        if (selectedHeroImage !== project.selectedHeroImage) {
            next = { ...next, selectedHeroImage };
            mutated = true;
        }
    }

    // Stage 5 keeps a snapshot of the authored scope inside quoteData. It is a
    // second persistence lane, not a display-only cache, so apply the same
    // byte-loss-free scrub there as the top-level builder scope. Missing this
    // lane let durable images carry multi-megabyte base64 twice and could still
    // trip the 50 MB backend limit after the top-level scope was clean.
    if (project.quoteData && typeof project.quoteData === 'object'
        && Array.isArray(project.quoteData.scope)) {
        const quoteScope = _walkScopeForInlinePhotos(project.quoteData.scope);
        if (quoteScope !== project.quoteData.scope) {
            next = { ...next, quoteData: { ...project.quoteData, scope: quoteScope } };
            mutated = true;
        }
    }
    const quoteDataForHero = next.quoteData;
    const quoteHeader = quoteDataForHero && typeof quoteDataForHero === 'object'
        ? quoteDataForHero.header
        : null;
    if (quoteHeader && typeof quoteHeader === 'object' && !Array.isArray(quoteHeader)
        && quoteHeader.heroImage && typeof quoteHeader.heroImage === 'object'
        && !Array.isArray(quoteHeader.heroImage)) {
        const heroImage = _stripPhotoInlineBytes(quoteHeader.heroImage);
        if (heroImage !== quoteHeader.heroImage) {
            next = {
                ...next,
                quoteData: {
                    ...quoteDataForHero,
                    header: { ...quoteHeader, heroImage },
                },
            };
            mutated = true;
        }
    }

    // selectedHeroImage and historical/future JSONB snapshots do not all live
    // in the named lanes above. Scrub the complete envelope last so no nested
    // image/video data URL can bypass the boundary guard.
    const recursivelyStripped = _stripNestedCustomerMediaDataUrls(next);
    if (recursivelyStripped !== next) return recursivelyStripped;
    return mutated ? next : project;
}

/**
 * The same fail-closed scrub as the project save, for any other JSON body the
 * backend's inline-customer-media policy guards (the sent-quote record). Never
 * mutates its input. A value that is entirely media comes back as null.
 */
export function stripInlineCustomerMediaForWire(value) {
    const stripped = _stripNestedCustomerMediaDataUrls(value);
    return stripped === STRIPPED_CUSTOMER_MEDIA ? null : stripped;
}

const LOCAL_PROJECT_STATUS_TO_BACKEND = Object.freeze({
    'pre-processed': 'draft',
    preprocessed: 'draft',
    ready_for_analysis: 'draft',
    'ready-for-analysis': 'draft',
    intake_complete: 'draft',
});

function _backendProjectStatus(status) {
    const raw = typeof status === 'string' ? status.trim() : status;
    return LOCAL_PROJECT_STATUS_TO_BACKEND[raw] || raw;
}

function _prepareProjectForBackendSave(project) {
    _assertCanonicalDocumentLaneDurable(project);
    const stripped = _stripInlineMediaForSave(project);
    const status = _backendProjectStatus(stripped?.status);
    return status !== stripped?.status ? { ...stripped, status } : stripped;
}

// Shared by the one-way Dexie migration, which POSTs project envelopes without
// going through DataService.projects.save. Keeping that path on this same
// boundary guard prevents migration failures from reintroducing inline media.
export function sanitizeProjectForBackendSave(project) {
    // This synchronous migration-only entrypoint cannot create Blob records.
    // Therefore it may sanitize only already-durable media; anything else
    // must make the migration fail visibly before its direct POST.
    _assertCanonicalPhotoLanesDurable(project);
    _assertCanonicalVideoLaneDurable(project);
    return _prepareProjectForBackendSave(project);
}

function _withProjectLifecycleAliases(project) {
    if (!project || typeof project !== 'object') return project;
    const context = project.aiContext && typeof project.aiContext === 'object'
        ? project.aiContext
        : {};
    const quoteData = project.quoteData ?? project.quote_data;
    const readinessReview = quoteData?.meta?.readinessReview ?? null;
    const fromContext = (camel, snake = camel) => context[camel] ?? context[snake];
    return {
        ...project,
        needsReview: readinessReview !== null,
        costingsSent: project.costingsSent ?? fromContext('costingsSent', 'costings_sent') ?? false,
        costingsSentAt: project.costingsSentAt ?? fromContext('costingsSentAt', 'costings_sent_at') ?? null,
        costingsSentTo: project.costingsSentTo ?? fromContext('costingsSentTo', 'costings_sent_to') ?? '',
        wonAt: project.wonAt ?? fromContext('wonAt', 'won_at') ?? null,
        lostAt: project.lostAt ?? fromContext('lostAt', 'lost_at') ?? null,
        lostReason: project.lostReason ?? fromContext('lostReason', 'lost_reason') ?? null,
        nextFollowUp: project.nextFollowUp ?? fromContext('nextFollowUp', 'next_follow_up') ?? null,
        nextFollowUpNote: project.nextFollowUpNote ?? fromContext('nextFollowUpNote', 'next_follow_up_note') ?? '',
    };
}

// A project summary keeps the snake_case read aliases apiFetch added
// (company_id, created_at, updated_at, quote_status and the rest). The
// lifecycle wrapper rebuilds the row with a spread, which copies enumerable
// keys only, so every snake_case reader of a summary got undefined online
// while the offline Dexie summary, which emits those keys, worked. Each alias
// stays non-enumerable and reads the value the wrapper settled on for its
// camelCase key. Summaries only: full-project results feed savers that test
// for own snake_case keys (quote_data, ai_context) and would then write them
// back as enumerable fields.
function _withProjectSummaryAliases(row) {
    const project = _withProjectLifecycleAliases(row);
    if (!project || typeof project !== 'object' || project === row) return project;
    for (const key of Object.getOwnPropertyNames(row)) {
        const camelKey = toCamelCase(key);
        if (camelKey === key
            || Object.prototype.propertyIsEnumerable.call(row, key)
            || Object.prototype.hasOwnProperty.call(project, key)) continue;
        Object.defineProperty(project, key, {
            value: Object.prototype.hasOwnProperty.call(project, camelKey) ? project[camelKey] : row[key],
            enumerable: false,
            configurable: true,
            writable: true,
        });
    }
    return project;
}

// A fresh deterministic intake and the mounted QuoteMaker editor can both
// reach this API boundary while the same local project is being registered.
// The hook-level save queue coalesces editor calls, but it cannot see the
// independent intake persistence lane. Share an in-flight POST only when the
// fully-sanitised JSON body is byte-for-byte identical. A changed title,
// stage, upload receipt, or any other edit always receives its own request.
//
// The server's inline-media rejection is deterministic for that exact body.
// Briefly retaining just that terminal 400 prevents the queued drain and an
// independent intake caller from replaying the same invalid body in a tight
// burst. Network/5xx/conflict failures are deliberately not latched.
const _PROJECT_SAVE_INLINE_MEDIA_FAILURE_LATCH_MS = 1500;
const _PROJECT_SAVE_REQUEST_CACHE_MAX_PROJECTS = 32;
const _projectSaveRequestCache = new Map();

function _projectSaveRequestBucketKey(wirePayload) {
    return String(wirePayload?.id || wirePayload?.dexieId || '<idless-project>');
}

function _removeProjectSaveRequestEntry(bucketKey, entry) {
    const entries = _projectSaveRequestCache.get(bucketKey);
    if (!entries) return;
    const remaining = entries.filter((candidate) => candidate !== entry);
    if (remaining.length) _projectSaveRequestCache.set(bucketKey, remaining);
    else _projectSaveRequestCache.delete(bucketKey);
}

function _pruneProjectSaveRequestCache(now = Date.now()) {
    for (const [bucketKey, entries] of _projectSaveRequestCache.entries()) {
        const remaining = entries.filter((entry) => (
            entry.promise
            || (entry.failedAt && now - entry.failedAt < _PROJECT_SAVE_INLINE_MEDIA_FAILURE_LATCH_MS)
        ));
        if (remaining.length) _projectSaveRequestCache.set(bucketKey, remaining);
        else _projectSaveRequestCache.delete(bucketKey);
    }

    if (_projectSaveRequestCache.size <= _PROJECT_SAVE_REQUEST_CACHE_MAX_PROJECTS) return;
    for (const [bucketKey, entries] of _projectSaveRequestCache.entries()) {
        if (entries.some((entry) => entry.promise)) continue;
        _projectSaveRequestCache.delete(bucketKey);
        if (_projectSaveRequestCache.size <= _PROJECT_SAVE_REQUEST_CACHE_MAX_PROJECTS) break;
    }
}

function _resetProjectSaveRequestCache() {
    _projectSaveRequestCache.clear();
}

function _postProjectSaveSingleflight(wirePayload, serialised) {
    const now = Date.now();
    _pruneProjectSaveRequestCache(now);
    const bucketKey = _projectSaveRequestBucketKey(wirePayload);
    const entries = _projectSaveRequestCache.get(bucketKey) || [];
    const existing = entries.find((entry) => entry.serialised === serialised);
    if (existing?.promise) return existing.promise;
    if (existing?.error
        && now - existing.failedAt < _PROJECT_SAVE_INLINE_MEDIA_FAILURE_LATCH_MS) {
        return Promise.reject(existing.error);
    }

    const entry = { serialised, promise: null, error: null, failedAt: 0 };
    const request = apiFetch('/projects', { method: 'POST', body: serialised });
    entry.promise = request.then(
        (saved) => {
            _removeProjectSaveRequestEntry(bucketKey, entry);
            return saved;
        },
        (error) => {
            entry.promise = null;
            if (error?.status === 400 && error?.code === 'INLINE_CUSTOMER_MEDIA_FORBIDDEN') {
                entry.error = error;
                entry.failedAt = Date.now();
            } else {
                _removeProjectSaveRequestEntry(bucketKey, entry);
            }
            throw error;
        },
    );
    entries.push(entry);
    _projectSaveRequestCache.set(bucketKey, entries);
    return entry.promise;
}

function _assertProjectQuoteRunClaimInput(projectId, claim, {
    requireVersion = false,
    allowTtl = true,
    requireOutcome = false,
} = {}) {
    if (!isUUID(projectId)) {
        throw new Error('Project Quote Run claim requires a UUID project id');
    }
    if (!isUUID(claim?.claimId)) {
        throw new Error('Project Quote Run claim requires a caller-generated UUID claimId');
    }
    if (requireVersion && (
        typeof claim?.expectedUpdatedAt !== 'string'
        || !Number.isFinite(Date.parse(claim.expectedUpdatedAt))
    )) {
        throw new Error('Project Quote Run claim requires a valid expectedUpdatedAt Project version');
    }
    if (allowTtl && claim?.ttlSeconds !== undefined && (
        !Number.isInteger(claim.ttlSeconds)
        || claim.ttlSeconds < 30
        || claim.ttlSeconds > 180
    )) {
        throw new Error('Project Quote Run claim ttlSeconds must be an integer from 30 to 180');
    }
    if (!allowTtl && claim?.ttlSeconds !== undefined) {
        throw new Error('Project Quote Run release does not accept ttlSeconds');
    }
    if (requireOutcome && !['complete', 'failed', 'cancelled', 'review-required'].includes(claim?.outcome)) {
        throw new Error('Project Quote Run release outcome must be complete, failed, cancelled or review-required');
    }
}

const projects = {
    /** @returns {Promise<ProjectEnvelope[]>} */
    async getAll() {
        const rows = await apiFetch('/projects');
        return Array.isArray(rows) ? rows.map(_withProjectLifecycleAliases) : [];
    },
    /**
     * @param {string} id
     * @returns {Promise<ProjectEnvelope>}
     */
    async getById(id) { return _withProjectLifecycleAliases(await apiFetch(`/projects/${id}`)); },
    async getVersion(id, options = {}) {
        if (!isUUID(id)) throw new Error('projects.getVersion requires a UUID project id');
        return apiFetch(`/projects/${encodeURIComponent(id)}/version`, {
            signal: options.signal,
        });
    },
    async acquireQuoteRunClaim(id, claim = {}, options = {}) {
        _assertProjectQuoteRunClaimInput(id, claim, { requireVersion: true });
        const body = {
            claimId: claim.claimId,
            expectedUpdatedAt: claim.expectedUpdatedAt,
        };
        if (claim.ttlSeconds !== undefined) body.ttlSeconds = claim.ttlSeconds;
        return apiFetch(`/projects/${encodeURIComponent(id)}/quote-run-claim`, {
            method: 'POST',
            body: JSON.stringify(body),
            signal: options.signal,
        });
    },
    async renewQuoteRunClaim(id, claim = {}, options = {}) {
        _assertProjectQuoteRunClaimInput(id, claim, { requireVersion: true });
        const body = {
            claimId: claim.claimId,
            expectedUpdatedAt: claim.expectedUpdatedAt,
        };
        if (claim.ttlSeconds !== undefined) body.ttlSeconds = claim.ttlSeconds;
        return apiFetch(`/projects/${encodeURIComponent(id)}/quote-run-claim`, {
            method: 'PATCH',
            body: JSON.stringify(body),
            signal: options.signal,
        });
    },
    async releaseQuoteRunClaim(id, claim = {}, options = {}) {
        _assertProjectQuoteRunClaimInput(id, claim, { allowTtl: false, requireOutcome: true });
        return apiFetch(`/projects/${encodeURIComponent(id)}/quote-run-claim`, {
            method: 'DELETE',
            body: JSON.stringify({ claimId: claim.claimId, outcome: claim.outcome }),
            signal: options.signal,
        });
    },
    /**
     * @param {ProjectSaveInput} project
     * @returns {Promise<ProjectEnvelope>}
     */
    async save(project) {
        // v5.12.2 (2026-04-22): always POST. Previously this routed to PUT
        // when project.id looked UUID-shaped, but Agent A's UUIDs-from-birth
        // pivot means EVERY new project has a UUID from creation. So the
        // PUT path fired for brand-new projects the backend had never seen
        // and Postgres returned 404. Backend POST is upsert-aware via
        // `dexie_id`: passing the frontend's stable id as `dexieId` lets
        // the backend match an existing row (UPDATE) or insert a fresh one
        // (INSERT with the same UUID honored as the PK: see
        // backend/src/routes/projects.js). The `id` stays in the body so
        // the backend's INSERT preserves it as the real PK, meaning the
        // frontend UUID === backend UUID from first save onward.
        const looksPartial = project?.id
            && !('title' in project)
            && !('formData' in project)
            && !('scope' in project)
            && !('currentStage' in project);
        let payload = project;
        if (looksPartial) {
            const existing = await apiFetch(`/projects/${project.id}`).catch(() => null);
            if (!existing) {
                throw new Error(`Refusing partial project save for missing project ${project.id}`);
            }
            // Carry an optimistic-concurrency token, always.
            //
            // This branch turns a one-lane patch into a FULL envelope by merging
            // it over the server row and POSTing that envelope. Ungated, it is a
            // blind overwrite: anything written between the GET above and the
            // POST below is silently reverted, and — worse — the version the
            // POST returns cannot then be adopted by the caller, because the
            // caller's in-memory copy may no longer describe the row it just
            // wrote. Adopting it would arm a stale tab to replay its copy over
            // another device's work.
            //
            // Prefer the caller's own token when it has one: it knows which
            // version its state descends from, so a refusal is the truthful
            // answer and the caller's adoption of the response is then sound.
            // Otherwise gate on the version we just merged from, which at least
            // narrows the blind window to the gap between these two calls.
            const mergedFromVersion = existing.updatedAt || existing.updated_at || null;
            payload = {
                ...existing,
                ...project,
                expectedUpdatedAt: project.expectedUpdatedAt ?? mergedFromVersion ?? undefined,
                updated_at: project.updated_at || new Date().toISOString(),
            };
        }

        // The scrub may remove bytes only after a durable browser/cloud source
        // exists. Direct intake and editor callers both cross this gate.
        payload = await _ensureProjectMediaDurableForSave(payload);

        // v5.41.3 hot-fix: defensive last-mile strip + size guard. See header above.
        // A legacy browser row is re-keyed to a UUID before any media write,
        // but its old dexieId remains the backend upsert lineage. Preserve that
        // explicit key so an existing cloud row is adopted (or a collision is
        // refused) instead of silently creating a second Project.
        const stableDexieId = payload.dexieId || payload.dexie_id || payload.id;
        const wirePayload = _prepareProjectForBackendSave({ ...payload, dexieId: stableDexieId });
        const serialised = JSON.stringify(wirePayload);
        const bytes = _byteSize(serialised);

        if (bytes >= PAYLOAD_WARN_THRESHOLD_BYTES) {
            _logProjectPayloadBreakdown(wirePayload, bytes);
        }
        if (bytes >= PAYLOAD_HARD_FAIL_BYTES) {
            const mb = (bytes / 1024 / 1024).toFixed(2);
            const top = _topPayloadFields(wirePayload, 3);
            const err = new Error(
                `Project save aborted client-side: payload is ${mb} MB which would exceed the backend body limit`
                + (top ? ` (largest fields: ${top})` : '')
                + `. Inline media that couldn't strip is the usual cause — see the console for a per-field breakdown.`,
            );
            err.code = 'CLIENT_PAYLOAD_TOO_LARGE';
            err.payloadBytes = bytes;
            throw err;
        }
        return _withProjectLifecycleAliases(await _postProjectSaveSingleflight(wirePayload, serialised));
    },
    async duplicate(sourceProjectId, targetProject, options = {}) {
        if (!isUUID(sourceProjectId)) {
            throw new Error('projects.duplicate requires a UUID source project id');
        }
        if (!isUUID(targetProject?.id)) {
            throw new Error('projects.duplicate requires a UUID target project id');
        }
        const expectedUpdatedAt = options.expectedUpdatedAt;
        if (typeof expectedUpdatedAt !== 'string' || !Number.isFinite(Date.parse(expectedUpdatedAt))) {
            throw new Error('projects.duplicate requires the source expectedUpdatedAt receipt');
        }
        return _withProjectLifecycleAliases(await post(`/projects/${sourceProjectId}/duplicate`, {
            targetProjectId: targetProject.id,
            title: targetProject.title,
            expectedUpdatedAt,
        }));
    },
    async delete(id) { return del(`/projects/${id}`); },
    async updateQuoteStatus(id, quoteStatus) {
        if (!isUUID(id)) return null; // Dexie-only id - skip API call, Dexie dual-write handles it
        return patch_(`/projects/${id}/quote-status`, { quoteStatus });
    },
    async recordPdfGenerated(id, detail = {}) {
        if (!isUUID(id)) throw new Error('projects.recordPdfGenerated requires a UUID project id');
        return _withProjectLifecycleAliases(await patch_(`/projects/${id}/pdf-generated`, {
            filename: detail.filename || null,
            bytes: detail.bytes ?? null,
            savedPath: detail.savedPath || null,
            source: detail.source || null,
        }));
    },
    async recordPrintOpened(id) {
        if (!isUUID(id)) throw new Error('projects.recordPrintOpened requires a UUID project id');
        return _withProjectLifecycleAliases(await patch_(`/projects/${id}/print-opened`, {}));
    },
    /**
     * Attach a project to its requester company and/or its property.
     *
     * Two foreign keys, so a two-uuid PATCH — NOT a full-envelope save. The CRM
     * migration used to do this by GETting the whole project and POSTing it
     * back; on the live RJ Bird tenant that was 126 re-POSTs totalling 236 MB
     * to write derived ids, and because a full save carries no
     * expectedUpdatedAt it was also a blind last-write-wins overwrite of
     * scope/form_data. See PATCH /:id/crm-link in backend/src/routes/projects.js.
     *
     * Only the keys you pass are written; omit one to leave that column alone.
     * The server treats this as a bookkeeping write, so `updatedAt` comes back
     * UNCHANGED — mirror it, don't invent one.
     *
     * @param {string} id
     * @param {{ companyId?: string|null, propertyId?: string|null }} link
     */
    async setCrmLink(id, link = {}) {
        if (!isUUID(id)) throw new Error('projects.setCrmLink requires a UUID project id');
        const body = {};
        if ('companyId' in link) body.companyId = link.companyId ?? null;
        if ('propertyId' in link) body.propertyId = link.propertyId ?? null;
        if (!('companyId' in body) && !('propertyId' in body)) {
            throw new TypeError('projects.setCrmLink requires companyId and/or propertyId');
        }
        // The RFQ layout the operator's choice teaches (E5 signal 5). Only a
        // fingerprint the server accepts is sent, so a choice is never refused for it.
        if (body.companyId && typeof link.formFingerprint === 'string' && /^[A-Za-z0-9:._-]{4,200}$/.test(link.formFingerprint)) {
            body.formFingerprint = link.formFingerprint;
        }
        return patch_(`/projects/${id}/crm-link`, body);
    },
    /**
     * Ask the server whether opening this job needs the client prompt (SPEC
     * 2026-10-08 D2, D3). The answer is { state }:
     *   'linked'      the job already has a client ({ company, propertyId }),
     *   'auto-linked' the requester email names exactly one firm, and the
     *                 server linked the job to it ({ company, evidence, propertyId }),
     * propertyId is the job's site on the server, null when it has none (the
     * link may drop a site that is not the firm's).
     *   'deferred'    "Match later" was pressed for this same email,
     *   'ask'         { suggestedFirm?, pdfFirmName?, candidates, unmanagedBuildings }.
     * The link is a bookkeeping write, so the save token does not move.
     * Matching never creates a company or a contact.
     *
     * @param {string} id
     * @param {{ email?: string, name?: string, pdfFirmName?: string, plan?: string }} input
     */
    async resolveCrmLink(id, input = {}) {
        if (!isUUID(id)) throw new Error('projects.resolveCrmLink requires a UUID project id');
        const body = {};
        for (const key of ['email', 'name', 'pdfFirmName', 'plan', 'formFingerprint']) {
            const value = typeof input?.[key] === 'string' ? input[key].trim() : '';
            if (value) body[key] = value;
        }
        // Decision E5 (2026-10-13): what the RFQ text names, sent only when present.
        for (const [key, max] of [['firmNames', 10], ['abns', 10], ['requesterNames', 20]]) {
            const list = Array.isArray(input?.[key])
                ? [...new Set(input[key].filter(item => typeof item === 'string').map(item => item.trim()).filter(Boolean))].slice(0, max)
                : [];
            if (list.length) body[key] = list;
        }
        return post(`/projects/${id}/crm-link/resolve`, body);
    },
    /**
     * "Match later": store on the job that its client is not chosen yet, with
     * the requester email the prompt answered (SPEC 2026-10-08 D3). Opening the
     * job does not ask again until that email changes. A bookkeeping write.
     *
     * @param {string} id
     * @param {string|null} email
     */
    async deferCrmLink(id, email) {
        if (!isUUID(id)) throw new Error('projects.deferCrmLink requires a UUID project id');
        const deferEmail = typeof email === 'string' && email.trim() ? email.trim() : null;
        return patch_(`/projects/${id}/crm-link`, { defer: true, deferEmail });
    },
    /**
     * The job's requesters as Clients holds them (2026-10-13): { projectId,
     * company: { id, name, companyType, generalEmail } | null, people,
     * linked: [{ contactId, role, person }], inherited }. Server only.
     *
     * @param {string} id
     */
    async getRequesters(id) {
        if (!isUUID(id)) throw new Error('projects.getRequesters requires a UUID project id');
        return apiFetch(`/projects/${id}/requesters`);
    },
    /**
     * Replace the job's requester links: [{ contactId, role: 'primary' | 'cc' }].
     * Each person must work for the job's client or a building it manages. A
     * bookkeeping write: the save token does not move. Answers like getRequesters.
     *
     * @param {string} id
     * @param {Array<{ contactId: string, role?: 'primary' | 'cc' }>} requesters
     */
    async setRequesters(id, requesters = []) {
        if (!isUUID(id)) throw new Error('projects.setRequesters requires a UUID project id');
        const body = (Array.isArray(requesters) ? requesters : [])
            .map(entry => ({ contactId: entry?.contactId, role: entry?.role === 'cc' ? 'cc' : 'primary' }));
        return put(`/projects/${id}/requesters`, { requesters: body });
    },
    async recordEmailSent(id, detail = {}) {
        if (!isUUID(id)) throw new Error('projects.recordEmailSent requires a UUID project id');
        return _withProjectLifecycleAliases(await patch_(`/projects/${id}/email-sent`, {
            finalSentScope: detail.finalSentScope,
        }));
    },
    async recordCostingsSent(id, detail = {}) {
        if (!isUUID(id)) throw new Error('projects.recordCostingsSent requires a UUID project id');
        return _withProjectLifecycleAliases(await patch_(`/projects/${id}/costings-sent`, {
            to: detail.to || null,
        }));
    },
    async recordOutcome(id, detail = {}) {
        if (!isUUID(id)) throw new Error('projects.recordOutcome requires a UUID project id');
        return _withProjectLifecycleAliases(await patch_(`/projects/${id}/outcome`, {
            status: detail.status,
            reason: detail.reason || null,
        }));
    },
    async recordFollowUp(id, detail = {}) {
        if (!isUUID(id)) throw new Error('projects.recordFollowUp requires a UUID project id');
        return _withProjectLifecycleAliases(await patch_(`/projects/${id}/follow-up`, {
            dueDate: detail.dueDate,
            note: detail.note || null,
        }));
    },
    async completeQuote(id, {
        expectedUpdatedAt,
        readyAt,
        scopeFingerprint,
        readinessReview = null,
    } = {}) {
        if (!isUUID(id)) throw new Error('projects.completeQuote requires a UUID project id');
        return patch_(`/projects/${id}/complete-quote`, {
            expectedUpdatedAt,
            readyAt,
            scopeFingerprint,
            readinessReview: sanitizeStage5ReadinessReview(readinessReview),
        });
    },
    async getAllSummaries() {
        const rows = await apiFetch('/projects?summary=true');
        return Array.isArray(rows) ? rows.map(_withProjectSummaryAliases) : [];
    },
    async cleanup() { return 0; } // Server handles cleanup
};

// UUID format check - PostgreSQL uses UUIDs, Dexie uses custom strings
const isUUID = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

// ============================================
// BUSINESS PRICING
// ============================================
const businessPricing = {
    // Failures PROPAGATE (2026-07-03). The old `.catch(() => null)` made a
    // backend 500 / network error indistinguishable from the backend's
    // legitimate 200+null "no row yet" — BusinessPricingService.getData()
    // then walked its seed path and answered with the populated DEFAULT
    // book, which the Pricing Console would happily edit + persist over the
    // operator's real rates. null still means "not configured"; an error now
    // reaches getData's per-caller policy (failHard callers surface it).
    async get() { return apiFetch('/business-pricing'); },
    async save(data) { return post('/business-pricing', data); }
};

// ============================================
// CLIENTS
// ============================================
const clients = {
    async getAll() { return apiFetch('/clients'); },
    async getById(id) { return apiFetch(`/clients/${id}`); },
    async save(client) {
        if (client.id && isUUID(client.id)) return put(`/clients/${client.id}`, client);
        const { id, ...rest } = client; return post('/clients', rest);
    },
    async delete(id) { return del(`/clients/${id}`); },
    async search(query) { return apiFetch(`/clients?q=${encodeURIComponent(query)}`); }
};

// ============================================
// PIPELINES
// ============================================
const pipelines = {
    async save(execution) { return post('/pipelines', execution); },
    async update(id, changes) { return put(`/pipelines/${id}`, changes); },
    async getByProject(projectId) { return apiFetch(`/pipelines?projectId=${projectId}`); },
    async getByPipeline(pipelineId) { return apiFetch(`/pipelines?pipelineId=${pipelineId}`); },
    async getAll() { return apiFetch('/pipelines'); },
    /** @param {{ cursor?: string | null, limit?: number | string }} [options] */
    async getHistory(options = {}) {
        const { cursor = null, limit = 25 } = options;
        const safeLimit = Math.min(25, Math.max(1, Number.parseInt(String(limit), 10) || 25));
        const params = new URLSearchParams({ limit: String(safeLimit) });
        if (cursor) params.set('cursor', String(cursor));
        return apiFetch(`/pipelines/history?${params.toString()}`);
    },
    async getHistoryDetail(id) {
        if (!id) throw new Error('Pipeline execution id is required.');
        return apiFetch(`/pipelines/history/${encodeURIComponent(String(id))}`);
    },
    /** @param {{ hours?: number | string, cursor?: string | null, limit?: number | string }} [options] */
    async getFailedRecent(options = {}) {
        const { hours = 24, cursor = null, limit = 25 } = options;
        const parsedHours = Number.parseInt(String(hours), 10);
        const parsedLimit = Number.parseInt(String(limit), 10);
        const safeHours = Number.isFinite(parsedHours)
            ? Math.min(72, Math.max(1, parsedHours))
            : 24;
        const safeLimit = Number.isFinite(parsedLimit) && parsedLimit >= 1
            ? Math.min(25, parsedLimit)
            : 25;
        const params = new URLSearchParams({
            hours: String(safeHours),
            limit: String(safeLimit),
        });
        if (cursor) params.set('cursor', String(cursor));
        return apiFetch(`/pipelines/failed-recent?${params.toString()}`);
    },
    async getStats() { return apiFetch('/pipelines/stats'); },
    async cleanup(days) { return del(`/pipelines/cleanup?days=${days || 30}`); }
};

// ============================================
// AUDIT TRAIL
// ============================================
const auditTrail = {
    async save(record) { return post('/audit-trail', record); },
    async getByProject(projectId) { return apiFetch(`/audit-trail?projectId=${projectId}`); },
    async getByProjectAndStage(projectId, stage) { return apiFetch(`/audit-trail?projectId=${projectId}&stage=${stage}`); }
};

// ============================================
// AI CONVERSATIONS
// ============================================
// Stored Assistant messages round-trip unchanged: their keys are not
// camelCased (spec 2026-10-07 section 5.2, F23). Row keys still are.
const CONVERSATION_OPAQUE_KEYS = ['messages', 'metadata'];
const conversationFetch = (path, options = {}) => apiFetch(path, { ...options, opaqueKeys: CONVERSATION_OPAQUE_KEYS });
const conversationPost = (path, body) => conversationFetch(path, { method: 'POST', body: JSON.stringify(body) });

const aiConversations = {
    // Legacy full-replace save. Kept for old callers only; the Assistant panel
    // appends and patches by message id instead (spec 2026-10-07, I2).
    async save(conv) { return post('/ai-conversations', conv); },
    // Scoped to a project when projectId is passed. Without one, returns the
    // most-recent unscoped (project_id IS NULL) conversation - i.e. a truly
    // "global" chat thread, not the most recent project's. Prevents the
    // assistant from leaking conversation between projects.
    //
    // Null means "no thread". A failed read THROWS (spec 2026-10-07, F15):
    // swallowing it to null made a 502 look like an empty project, and the
    // caller then archived the project's real conversation.
    async getCurrent(projectId = null) {
        const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
        return conversationFetch(`/ai-conversations/current${qs}`);
    },

    // ── Threads by job (spec 2026-10-07 section 5.1 / 5.2) ─────────────────
    /** The job's active thread, created when it has none. Throws on failure. */
    async openProjectThread(projectId) {
        if (!projectId || !isUUID(String(projectId))) {
            const err = new Error('This job has no saved id yet, so its conversation cannot be opened.');
            err.status = 400;
            throw err;
        }
        return conversationFetch(`/projects/${encodeURIComponent(projectId)}/conversation`);
    },
    /** The signed-in person's own platform thread, created when absent. */
    async openPlatformThread() {
        return conversationFetch('/conversations/global');
    },
    /** One thread by id, any status the caller can reach. */
    async getThread(id) {
        return conversationFetch(`/ai-conversations/${encodeURIComponent(id)}`);
    },
    /** Thread summaries (no messages) with their job, for History. */
    async listSummaries({ projectId = null, scope = 'project', limit = 50 } = {}) {
        const params = new URLSearchParams();
        if (projectId) params.set('projectId', String(projectId));
        params.set('scope', scope === 'all' ? 'all' : 'project');
        params.set('limit', String(Math.max(1, Math.min(200, Number(limit) || 50))));
        return conversationFetch(`/ai-conversations/summaries?${params.toString()}`);
    },
    /** Atomic append; the server skips ids it already has. */
    async appendMessages(id, messages) {
        return post(`/ai-conversations/${encodeURIComponent(id)}/messages`, { messages });
    },
    /** Shallow-merge a patch into one message by id. */
    async patchMessage(id, messageId, patch) {
        return patch_(`/ai-conversations/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}`, { patch });
    },
    /** Store the thread's summary of its turns up to and including throughMessageId. */
    async setCompaction(id, { summary, throughMessageId } = {}) {
        return conversationPost(`/ai-conversations/${encodeURIComponent(id)}/compaction`, { summary, throughMessageId });
    },
    async getByProject(projectId) {
        if (!projectId) return [];
        // A failed read rejects (I6, F15). Answering [] made it look like a
        // job with no conversation. The one caller, the folder projector,
        // already treats a rejection as "leave the conversation file out".
        return apiFetch(`/ai-conversations?projectId=${encodeURIComponent(projectId)}`);
    },
    async getAll() { return apiFetch('/ai-conversations'); },
    async getRecent(limit) { return apiFetch(`/ai-conversations?limit=${limit || 20}`); },
    async delete(id) { return del(`/ai-conversations/${id}`); },
    async clearAll() { return del('/ai-conversations'); },

    // ── Wave-2 Ship 2 additions ───────────────────────────────────────────
    // Hit the new /api/projects/:projectId/conversation* surface. The
    // methods match the Dexie-side aiConversations shape so the proxy in
    // data-service.js can dual-write under the same name.
    //
    // `convOrId` may be a full conversation object (we read projectId off it)
    // or an object shaped as { projectId, id }. When projectId is null, the
    // call hits the global /api/conversations/global surface.

    async append(convOrId, message, tokenDelta = 0) {
        const projectId = convOrId?.projectId ?? convOrId?.project_id ?? null;
        const path = projectId
            ? `/projects/${encodeURIComponent(projectId)}/conversation/messages`
            : `/conversations/global/messages`;
        return post(path, {
            message,
            tokenDelta,
            page: convOrId?.page || message?.pageContext?.page || null,
            stage: convOrId?.stage ?? message?.pageContext?.stage ?? null,
        });
    },

    async compact(convOrId, { summary = null, compactedUpTo = null, tokenCount = null } = {}) {
        if (typeof localStorage !== 'undefined'
            && localStorage.getItem('qm_disable_assistant_compaction') === '1') {
            return null;
        }
        const projectId = convOrId?.projectId ?? convOrId?.project_id;
        if (!projectId) return null;
        return post(`/projects/${encodeURIComponent(projectId)}/conversation/compact`, {
            summary, compactedUpTo, tokenCount,
        });
    },

    async archive(convOrId) {
        const id = convOrId?.id || (typeof convOrId === 'string' ? convOrId : null);
        if (!id || !isUUID(String(id))) return null;
        return conversationPost(`/ai-conversations/${encodeURIComponent(id)}/archive`, {});
    },

    async restore(convOrId) {
        const id = convOrId?.id || (typeof convOrId === 'string' ? convOrId : null);
        if (!id || !isUUID(String(id))) return null;
        return conversationPost(`/ai-conversations/${encodeURIComponent(id)}/restore`, {});
    },

    // The qm_disable_assistant_persistence switch is retired (QA round 3):
    // with the backend on, the server is the only source of a conversation
    // (spec I6), so New and Clear always reach /reset.
    async createNew(projectId = null) {
        const path = projectId
            ? `/projects/${encodeURIComponent(projectId)}/conversation/reset`
            : `/conversations/global/reset`;
        return conversationPost(path, {});
    },
};

// ============================================
// COMPANIES (CRM)
// ============================================
const companies = {
    async getAll() { return apiFetch('/companies?limit=2000'); },
    async getById(id) { return apiFetch(`/companies/${id}`); },
    async getHistory(id) { return apiFetch(`/companies/${encodeURIComponent(id)}/history`); },
    async unmatchedRequests() { return apiFetch('/companies/unmatched-requests'); },
    async linkRequest(payload) { return post('/companies/link-request', payload); },
    async save(company) {
        if (company.id && isUUID(company.id)) return put(`/companies/${company.id}`, company);
        const { id, ...rest } = company; return post('/companies', rest);
    },
    async delete(id) { return del(`/companies/${id}`); },
    async search(query) { return apiFetch(`/companies?q=${encodeURIComponent(query || '')}`); },
    async findDuplicateCandidates() { return post('/companies/duplicates', {}); },
    // Matching is read-only; findOrCreate requires an existing ID or explicit
    // creation confirmation. The backend serialises identity decisions.
    async findOrCreate(payload) { return post('/companies/find-or-create', payload); },
    async match(payload) { return post('/companies/match', payload); },
    async previewMerge(keepId, mergeId) {
        return post('/companies/preview-merge', { keepId, mergeId });
    },
    async mergeCompanies(keepId, mergeId) {
        const result = await post('/companies/merge', { keepId, mergeId });
        return {
            ...result,
            keepId: result.keptCompanyId,
            mergeId: result.archivedCompanyId ?? result.deletedCompanyId,
            mergeName: result.deletedCompanyName,
        };
    },
    // Tidy up (spec 2026-09-23 section 3). Proposals are read only; apply runs
    // each action in its own server transaction and reports every one by index.
    async cleanupProposals() { return apiFetch('/companies/cleanup-proposals'); },
    async applyCleanup(actions) { return post('/companies/cleanup/apply', { actions }); },
    // Owners corporations managed by a firm, with site/quote/contact counts.
    async managed(id) { return apiFetch(`/companies/${encodeURIComponent(id)}/managed`); },
    // Link (or clear with null) an owners corporation's managing firm. The
    // general PUT /companies/:id ignores the manager, so a stale whole-row
    // profile save can never undo a link; only this route changes it.
    async setManager(id, managingCompanyId) {
        return put(`/companies/${encodeURIComponent(id)}/manager`, { managingCompanyId: managingCompanyId ?? null });
    },
    // The record's Type control. The general PUT refuses to take a row out of
    // body_corporate; this route can, once the building is unlinked.
    async setType(id, companyType) {
        return put(`/companies/${encodeURIComponent(id)}/type`, { companyType });
    },
    // [{ id, name, companyType }]: the firms a building may be linked to, by
    // the same predicate the manager-link routes enforce.
    async firmCandidates() { return apiFetch('/companies/firm-candidates'); },
};

// ============================================
// COMPANY ADDRESSES (CRM)
// ============================================
const companyAddresses = {
    // The server has no /companies/:id/addresses route; GET /company-addresses reads companyId from the query.
    async getByCompany(companyId) { return apiFetch(`/company-addresses?companyId=${encodeURIComponent(companyId)}`); },
    async save(addr) { return post('/company-addresses', addr); },
    async delete(id) { return del(`/company-addresses/${id}`); },
};

// ============================================
// CRM CONTACTS
// ============================================
const crmContacts = {
    async getAll() { return apiFetch('/crm-contacts'); },
    async getById(id) { return apiFetch(`/crm-contacts/${id}`); },
    async getByCompany(companyId) { return apiFetch(`/crm-contacts?companyId=${companyId}`); },
    async save(contact) {
        if (contact.id && isUUID(contact.id)) return put(`/crm-contacts/${contact.id}`, contact);
        const { id, ...rest } = contact; return post('/crm-contacts', rest);
    },
    async delete(id) { return del(`/crm-contacts/${id}`); },
    async search(query) { return apiFetch(`/crm-contacts?q=${encodeURIComponent(query || '')}`); },
};

// ============================================
// CRM ACTIVITIES
// ============================================
const crmActivities = {
    async log(activity) { return post('/crm-activities', activity); },
    async getByCompany(companyId, limit) { return apiFetch(`/crm-activities?companyId=${companyId}&limit=${limit || 50}`); },
    async getByProject(projectId) { return apiFetch(`/crm-activities?projectId=${projectId}`); },
    async getPendingFollowUps() { return apiFetch('/crm-activities/follow-ups'); },
    // PUT /:id and POST /:id/complete both mark a follow-up done. The
    // POST endpoint is idempotent (returns `already_completed: true` on
    // double-fire) so prefer it from UI paths where retries are possible.
    async completeFollowUp(id) { return put(`/crm-activities/${id}`, {}); },
    async completeActivity(id) { return post(`/crm-activities/${id}/complete`, {}); },
};

// ============================================
// PROPERTIES (Sites/Buildings)
// ============================================
const properties = {
    async getAll() { return apiFetch('/properties?limit=2000'); },
    async getById(id) { return apiFetch(`/properties/${id}`); },
    async getByCompany(companyId) { return apiFetch(`/properties?companyId=${companyId}`); },
    async getByStrataplan(sp) { return apiFetch(`/properties?strata=${encodeURIComponent(sp || '')}`); },
    async save(property) {
        if (property.id && isUUID(property.id)) return put(`/properties/${property.id}`, property);
        const { id, ...rest } = property; return post('/properties', rest);
    },
    async delete(id) { return del(`/properties/${id}`); },
    async search(query) { return apiFetch(`/properties?q=${encodeURIComponent(query || '')}`); },
    async findByAddress(address) { return apiFetch(`/properties?address=${encodeURIComponent(address || '')}`); },
};

// ============================================
// PROPERTY CONTACTS (Junction)
// ============================================
const propertyContacts = {
    async getByProperty(propertyId) { return apiFetch(`/properties/${propertyId}/contacts`); },
    async getByContact(contactId) { return apiFetch(`/property-contacts?contactId=${contactId}`); },
    async save(record) { return post(`/properties/${record.property_id}/contacts`, record); },
    async delete(id) { return del(`/property-contacts/${id}`); },
    async deleteByProperty(propertyId) { return del(`/property-contacts?propertyId=${propertyId}`); },
};

// ============================================
// FOUNDER STRATEGIES (Strategic Brain v12)
// ============================================
const founderStrategies = {
    async save(strategy) { return post('/founder-strategies', strategy); },
    async getAll() { return apiFetch('/founder-strategies'); },
    async getActive() { return apiFetch('/founder-strategies/active'); },
    async getById(id) { return apiFetch(`/founder-strategies/${id}`); },
    async getByCategory(category) { return apiFetch(`/founder-strategies?category=${category}`); },
    async delete(id) { return del(`/founder-strategies/${id}`); },
    async toggleActive(id) { return post(`/founder-strategies/${id}/toggle`); },
    async incrementUseCount(id) { return post(`/founder-strategies/${id}/increment-use`); },
    async getStats() { return apiFetch('/founder-strategies/stats'); },
};

// ============================================
// BRAIN TELEMETRY (Strategic Brain v12)
// ============================================
const brainTelemetry = {
    async save(record) { return post('/brain-telemetry', record); },
    async getByProject(projectId) { return apiFetch(`/brain-telemetry/by-project/${projectId}`); },
    async getRecent(limit = 50) { return apiFetch(`/brain-telemetry?limit=${limit}`); },
    async getAll() { return apiFetch('/brain-telemetry'); },
    async getStats() { return apiFetch('/brain-telemetry/stats'); },
};

// ============================================
// CRM INSIGHTS (missing namespace - prevents split-brain)
// ============================================
const crmInsights = {
    async save(insight) { return post('/crm-insights', insight); },
    async getByClient(clientId) { return apiFetch(`/crm-insights/by-client/${clientId}`); },
    async getAll() { return apiFetch('/crm-insights'); },
    async delete(id) { return del(`/crm-insights/${id}`); },
};

const insightRuns = {
    async save(run) { return post('/crm-insights/runs', run); },
    async getAll() { return apiFetch('/crm-insights/runs'); },
};

const insightInteractions = {
    async save(interaction) { return post('/crm-insights/interactions', interaction); },
    async getAll() { return apiFetch('/crm-insights/interactions'); },
};

// Sync projection state is intentionally absent here. The authoritative
// durable receipt is /api/sync/v2/current; DataService.syncState is only the
// browser's post-commit cache and therefore stays Dexie-local.
const promptVersions = {
    async save(version) { return post('/prompt-versions', version); },
    async getAll() { return apiFetch('/prompt-versions'); },
    async getByPrompt(promptId) { return apiFetch(`/prompt-versions/by-prompt/${promptId}`); },
};

// ============================================
// BIOS (M2 Polish - cloud sync for business / client / strata bio rows)
// ============================================
// Mirrors the bio-service.js Dexie surface on the backend. Each entity
// returns the FULL row so the frontend can reconcile without a second
// fetch. PUT is an upsert keyed by id. Tenant isolation is enforced
// server-side via the composite (tenant_id, id) PK in migration 015.
//
// `data` is sent as a WRAPPED envelope (`{ data: {...}, provisional, ... }`)
// because the backend route rejects flat payloads (bios.js validateBioData).
// The bio-service.js helpers that call these endpoints assemble the
// envelope before invoking.
const bios = {
    business: {
        async get() { return apiFetch('/bios/business').catch(() => null); },
        async save(payload) { return put('/bios/business', payload); },
    },
    client: {
        async getAll(companyId = null) {
            const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
            return apiFetch(`/bios/client${qs}`);
        },
        async getById(id) { return apiFetch(`/bios/client/${id}`); },
        async save(id, payload) { return put(`/bios/client/${encodeURIComponent(id)}`, payload); },
        async delete(id) { return del(`/bios/client/${encodeURIComponent(id)}`); },
    },
    strata: {
        async getAll({ strataPlan = null, address = null } = {}) {
            const params = new URLSearchParams();
            if (strataPlan) params.set('strataPlan', strataPlan);
            if (address) params.set('address', address);
            const qs = params.toString() ? `?${params.toString()}` : '';
            return apiFetch(`/bios/strata${qs}`);
        },
        async getById(id) { return apiFetch(`/bios/strata/${id}`); },
        async save(id, payload) { return put(`/bios/strata/${encodeURIComponent(id)}`, payload); },
        async delete(id) { return del(`/bios/strata/${encodeURIComponent(id)}`); },
    },
};

// ============================================
// PLAUSIBILITY RESOLUTIONS (v5.12.21)
// ============================================
// Postgres mirror of the Dexie v21 plausibility_resolutions store. Lets
// dismissal decisions survive cloud restore + cross-device work. The
// Dexie-side API (data-service.js) exposes { get, put, listByProject,
// delete }. This shim adopts the same shape so the upstream proxy
// selects between Dexie and backend transparently.
const plausibilityResolutions = {
    async get(projectId, flagHash) {
        if (!projectId || !flagHash) return null;
        const params = new URLSearchParams({ projectId });
        const rows = await apiFetch(`/plausibility-resolutions?${params.toString()}`);
        return (Array.isArray(rows) ? rows : []).find(r => r.flagHash === flagHash || r.flag_hash === flagHash) || null;
    },
    async put(record) {
        if (!record || !record.projectId || !record.flagHash) return null;
        const saved = await post('/plausibility-resolutions', record);
        return saved?.id || null;
    },
    async listByProject(projectId) {
        if (!projectId) return [];
        const params = new URLSearchParams({ projectId });
        return apiFetch(`/plausibility-resolutions?${params.toString()}`);
    },
    async delete(id) {
        if (!id) return;
        return del(`/plausibility-resolutions/${encodeURIComponent(id)}`);
    },
};

// ─── File preview ────────────────────────────────────────────────────────
// Controlled preview for PDF + DOCX (Work Order E). Backed by
// `GET /api/files/:id/preview`, which returns either:
//   { previewType: 'inline', url, expiresAt, contentType }      — PDF / image
//   { previewType: 'docx',   url, expiresAt, contentType }      — DOCX (attachment)
//   { previewType: 'unsupported', url: null, message }          — other MIMEs
//
// Raw /download stays attachment-first for non-images; use that path for
// direct download. The preview URLs are short-lived (<= 300 s) and the
// useFilePreview hook handles one-shot auto-retry on 403.
const files = {
    /**
     * Fetch preview metadata for a file_metadata row. Tenant-verified; 404
     * on cross-tenant or missing id (does not leak existence).
     * @param {string} fileId - file_metadata UUID
     */
    async getPreviewMeta(fileId) {
        if (!fileId) throw new Error('files.getPreviewMeta: fileId required');
        return apiFetch(`/files/${encodeURIComponent(fileId)}/preview`);
    },

    /**
     * Fetch the bytes at a short-lived signed URL. Returns an ArrayBuffer.
     * The JWT is NOT required on the signed URL itself (the signature is
     * the auth), but we keep credentials:'omit' to avoid accidentally
     * attaching cookies intended for the app origin.
     * Throws with `.status` attached so useFilePreview can detect 403 expiry.
     *
     * Polish 20 (2026-05-12): if the direct MinIO fetch fails with a
     * network error (TypeError — typical for ERR_CONNECTION_REFUSED when
     * the browser cannot reach the host-published MinIO port), fall back
     * to the authenticated `/files/direct-download` route which streams
     * through the backend (always reachable via Docker-internal network).
     * After the first failure we keep a short cooldown so subsequent
     * fetches skip the direct attempt entirely.
     *
     * @param {string} signedUrl
     */
    async fetchPreviewBytes(signedUrl) {
        if (!signedUrl) throw new Error('files.fetchPreviewBytes: signedUrl required');

        // Skip the direct attempt if we recently saw a network failure on
        // the host-published MinIO port. Goes straight to the backend
        // proxy fallback.
        if (!directMinioFetchPaused()) {
            try {
                const response = await fetch(signedUrl, {
                    method: 'GET',
                    credentials: 'omit',
                });
                if (response.ok) return response.arrayBuffer();
                // Non-2xx response: NOT a network failure — return the
                // 403/404/etc to the caller so preview-expiry handling
                // (useFilePreview) can re-request a fresh URL. Don't
                // pause direct mode here.
                //
                // UF-CP-3 (v5.19.63): also lift the structured `code` field
                // off the JSON body when present (e.g. 410 + FILE_ORPHAN
                // from Council #2 / Fix B). Callers (Stage 2 PDF preview,
                // FileOrphanBanner) branch on err.code without re-reading
                // the body. Reading body is best-effort — body may not be
                // JSON for some error paths.
                const err = new Error(`Preview bytes fetch failed (${response.status})`);
                err.status = response.status;
                try {
                    const body = await response.clone().json();
                    if (body && typeof body === 'object') {
                        if (body.code) err.code = body.code;
                        if (body.error && !err.message) err.message = body.error;
                    }
                } catch { /* non-JSON body — leave err.code unset */ }
                throw err;
            } catch (err) {
                // TypeError on `fetch()` = TCP failure (CONNECTION_REFUSED,
                // DNS, CORS preflight reject). Anything with `.status`
                // attached above is a non-2xx response and must not pause
                // direct mode.
                if (err && typeof err.status === 'number') throw err;
                markDirectMinioFetchUnavailable(err?.message || 'network error');
                // Fall through to the backend proxy.
            }
        }

        const s3Key = extractS3KeyFromSignedUrl(signedUrl);
        if (!s3Key) {
            const err = new Error('Preview bytes fetch failed: unable to derive s3Key for backend fallback');
            err.status = 0;
            throw err;
        }
        return fetchPreviewBytesViaBackend(s3Key);
    },
};

// ── Polish 20 (2026-05-12): browser-proxy fallback for MinIO GETs ───
// Mirror of file-upload-service.js's upload fallback. State + helpers
// kept here (rather than imported from upload service) to avoid
// cross-module coupling on a small surface.

const DIRECT_MINIO_FETCH_FALLBACK_COOLDOWN_MS = 60_000;
const DIRECT_MINIO_FETCH_LOG_INTERVAL_MS = 30_000;
let directMinioFetchPausedUntil = 0;
let lastDirectMinioFetchLogAt = 0;

function directMinioFetchPaused() {
    return Date.now() < directMinioFetchPausedUntil;
}

function markDirectMinioFetchUnavailable(reason) {
    directMinioFetchPausedUntil = Math.max(
        directMinioFetchPausedUntil,
        Date.now() + DIRECT_MINIO_FETCH_FALLBACK_COOLDOWN_MS,
    );
    const now = Date.now();
    if (now - lastDirectMinioFetchLogAt < DIRECT_MINIO_FETCH_LOG_INTERVAL_MS) return;
    lastDirectMinioFetchLogAt = now;
    const suffix = reason ? ` (${reason})` : '';
    console.info(`[FilePreview] Direct MinIO fetch unavailable${suffix}; using backend download fallback.`);
}

const FILES_BUCKET_PATH_PREFIX = '/quotemaker-files/';

function extractS3KeyFromSignedUrl(signedUrl) {
    try {
        const parsed = new URL(signedUrl);
        if (!parsed.pathname.startsWith(FILES_BUCKET_PATH_PREFIX)) return null;
        return decodeURIComponent(parsed.pathname.slice(FILES_BUCKET_PATH_PREFIX.length));
    } catch {
        return null;
    }
}

async function fetchPreviewBytesViaBackend(s3Key) {
    // apiFetch is JSON-only; for binary streams we do an authenticated
    // fetch directly. Reuses the same token-refresh helpers so a stale
    // access token gets renewed before the request fires.
    let token = getAccessToken();
    if (token && isTokenExpired(token)) {
        try {
            token = await refreshAccessToken();
        } catch {
            token = getAccessToken();
        }
    }
    if (!token) {
        try {
            await devAutoLogin();
            token = getAccessToken();
        } catch {
            token = getAccessToken();
        }
    }
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const params = new URLSearchParams({ s3Key });
    const url = `${API_BASE}/files/direct-download?${params.toString()}`;
    let response = await fetch(url, { method: 'GET', headers });

    // 401 once-only refresh + retry (mirrors apiFetch's first leg)
    if (response.status === 401 && token) {
        try {
            const fresh = await refreshAccessToken();
            headers['Authorization'] = `Bearer ${fresh}`;
            response = await fetch(url, { method: 'GET', headers });
        } catch {
            // fall through; the 401 propagates below
        }
    }

    if (!response.ok) {
        const err = new Error(`Preview bytes fetch failed via backend (${response.status})`);
        err.status = response.status;
        // UF-CP-3 (v5.19.63): mirror the direct-MinIO branch and lift
        // `code` off the JSON body when present so FILE_ORPHAN propagates
        // through the proxy fallback too.
        try {
            const body = await response.clone().json();
            if (body && typeof body === 'object' && body.code) err.code = body.code;
        } catch { /* non-JSON body */ }
        throw err;
    }
    return response.arrayBuffer();
}

// ============================================
// TENANT PROFILE (v5.13.0: Workstream B3)
// ============================================
// Per-tenant business identity (legal name, ABN, licence, staff-identity
// domains, bank details, branding). Backs the Settings \u2192 Business Profile
// UI and the instance-owner tenant block injected into contact-extraction.
// Schema lives in backend/migrations/029_tenant_profile.sql.
const tenantProfile = {
    async get() { return apiFetch('/tenant-profile').catch(() => null); },
    /**
     * Throwing sibling of get(). SPEC-settings.md §7: get() swallows every
     * failure to null, and rowToForm(null) renders an EMPTY, fully-editable
     * profile — so an offline load followed by a Save OVERWRITES the real row
     * with empty strings and nulls the ABN. The Settings page's server band
     * must be able to tell "no profile yet" (null body) from "could not load"
     * (throw), and only this variant can. get() stays unchanged for existing
     * callers whose fallback-to-defaults behaviour is correct.
     */
    async getOrThrow() { return apiFetch('/tenant-profile'); },
    async put(profile) { return apiFetch('/tenant-profile', { method: 'PUT', body: JSON.stringify(profile) }); },
};

// ─── AI Gateway ──────────────────────────────────────────────────────────
// WO-C (v5.13.1). Backend-owned AI call routing. Every AI request from the
// frontend in default (backend) mode flows through these methods, which POST
// to the authenticated /api/ai/* endpoints. The backend owns provider
// credentials and routing, enforces per-tenant policy, and writes a row to
// ai_usage_events for every call (success AND failure).
//
// `feature` is REQUIRED — a short label naming the product surface making
// the call (e.g. 'stage2_analysis', 'wms', 'assistant', 'quote_gen'). The
// backend returns 400 if absent.
//
// Routing: production sessions always use the backend gateway (fail closed).
// As of TS-014 (v5.41.22) localStorage.qm_disable_ai_gateway no longer routes
// the frontend browser-direct on its own. The browser-direct path is local-dev
// only and requires a dev build PLUS VITE_QM_AI_GATEWAY_MODE='browser' (or
// 'hybrid', which tries the backend first) PLUS localStorage
// qm_allow_browser_provider_key='true'. See src/services/ai-providers.js.
//
// Section ownership is structurally independent from the core CRUD code that
// WO-A edits so the orchestrator merge is trivial.
/**
 * Nothing paid is sent into a server that is not answering (outage lane,
 * 2026-09-29: harvest, its fallback, embeddings and the Assistant each fired
 * into a 502 for ten minutes). Free while the server answers: the gate is a
 * flag read. While it does not, one shared /api/health probe decides.
 */
async function assertBackendAnsweringForAi(label) {
    if (await ensureBackendAnswering()) return;
    throw createServerUnavailableError({ label, method: 'POST' });
}

const aiGateway = {
    /**
     * Non-streaming chat completion via the backend gateway.
     * @param {Object} body
     * @param {string} body.feature      REQUIRED label
     * @param {string} body.model
     * @param {Array}  body.messages
     * @param {number} [body.temperature]
     * @param {number} [body.maxTokens]
     * @param {Array}  [body.tools]
     * @param {string|Object} [body.toolChoice]
     * @param {Object} [body.responseFormat]
     * @param {string} [body.projectId]
     * @param {Object} [body.metadata]    Free-form ledger metadata
     * @param {AbortSignal} [body.signal]
     * @param {string} [body.endpoint]   Default '/ai/chat'; alternatives:
     *                                   '/ai/chat-with-tools', '/ai/chat/vision'
     * @returns {Promise<{ content: string|null, toolCalls: Array|null, finishReason: string|null, usage: Object, provider?: string, providerRoute?: Object, providerFallback?: Object|null }>}
     */
    async chat(body = {}) {
        if (!body.feature) throw new Error('aiGateway.chat: feature is required');
        await assertBackendAnsweringForAi('AI request');
        const { signal, endpoint, task, evaluationRequestPermit, ...rest } = body;
        // Resolve per-task reasoning effort for reasoning models. The prod gateway
        // path otherwise NEVER sends it (buildLegacyBody only runs in dev
        // browser-direct mode), so without this "Think Harder" / diagnosis
        // reasoning never reaches OpenRouter. KEY OFF THE REAL TASK NAME (the
        // hyphenated TASK_DEFAULTS key, e.g. 'ai-assistant'), NOT the underscored
        // pipeline `feature` label (e.g. 'ai_assistant', via _pipelineToFeature)
        // which never matched the registry — that mismatch silently disabled
        // reasoning on every prod call before v5.46.44. `task` is also forwarded
        // as semantic audit context; `feature` remains the policy/ledger label.
        // resolveReasoningEffort gates on the kill switch + reasoning
        // model + task default; the backend relays rest.reasoningEffort -> body.reasoning.
        if (!rest.governedModelRoute && !rest.governedSupportRoute && rest.reasoningEffort == null) {
            // Effort tier ('low'..'xhigh') OR the 'none' suppression sentinel
            // (backend -> reasoning:{enabled:false}) for reason-by-default models
            // on strict-JSON tasks. See model-config.resolveReasoningDirective.
            const directive = resolveReasoningDirective(task || rest.feature, rest.model);
            if (directive) rest.reasoningEffort = directive;
        }
        const path = endpoint || '/ai/chat';
        const wireBody = task ? { ...rest, task } : rest;
        return apiFetch(path, {
            method: 'POST',
            body: JSON.stringify(wireBody),
            signal,
            ...(evaluationRequestPermit ? {
                headers: { 'X-QM-Eval-AI-Permit': evaluationRequestPermit },
            } : {}),
        });
    },

    /**
     * Streaming chat completion. Returns the raw Response object so the
     * caller can parse SSE chunks (parseSSEStream in ai-api.js). Includes
     * JWT + proactive refresh. Not routed through apiFetch() because
     * apiFetch JSON-decodes the body; streaming needs the live body.
     *
     * The backend emits OpenAI-compatible `data: {...}` chunks.
     */
    async stream(body = {}) {
        if (!body.feature) throw new Error('aiGateway.stream: feature is required');
        await assertBackendAnsweringForAi('AI request');
        const { signal, task, evaluationRequestPermit, ...rest } = body;
        // Same per-task reasoning forwarding as chat() above — key off the real
        // hyphenated `task`, fall back to `feature`. Keep task on the wire so
        // streaming ledger entries retain the same semantic attribution as
        // non-stream calls.
        if (!rest.governedModelRoute && !rest.governedSupportRoute && rest.reasoningEffort == null) {
            const directive = resolveReasoningDirective(task || rest.feature, rest.model);
            if (directive) rest.reasoningEffort = directive;
        }
        const url = `${API_BASE}/ai/chat/stream`;
        const wireBody = task ? { ...rest, task } : rest;

        // Proactive refresh so a 401 doesn't arrive mid-stream.
        let token = getAccessToken();
        if (token && isTokenExpired(token)) {
            try { token = await refreshAccessToken(); } catch { token = getAccessToken(); }
        }
        const headers = {
            'Content-Type': 'application/json',
            ...(evaluationRequestPermit
                ? { 'X-QM-Eval-AI-Permit': evaluationRequestPermit }
                : {}),
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        let response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(wireBody),
            signal,
        });
        // Authentication transport only: this rejected 401 never reaches the
        // AI route handler or a paid provider. The accepted re-fetch must still
        // return the evaluator's upstream single-attempt proof.
        if (response.status === 401 && token) {
            try {
                const newToken = await refreshAccessToken();
                headers['Authorization'] = `Bearer ${newToken}`;
                response = await fetch(url, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(wireBody),
                    signal,
                });
            } catch {
                // fall through; caller handles !response.ok
            }
        }
        return response;
    },

    /**
     * v5.13.14 — embeddings via the backend gateway.
     *
     * @param {Object} body
     * @param {string} body.feature        REQUIRED label e.g. 'semantic_retrieval'
     * @param {string} body.model
     * @param {string|string[]} body.input
     * @param {number} [body.dimensions]
     * @param {string} [body.projectId]
     * @param {AbortSignal} [body.signal]
     * @returns {Promise<{embeddings: number[][], usage: Object}>}
     */
    async embeddings(body = {}) {
        if (!body.feature) throw new Error('aiGateway.embeddings: feature is required');
        await assertBackendAnsweringForAi('Embedding request');
        const { signal, evaluationRequestPermit, ...rest } = body;
        return apiFetch('/ai/embeddings', {
            method: 'POST',
            body: JSON.stringify(rest),
            signal,
            ...(evaluationRequestPermit ? {
                headers: { 'X-QM-Eval-AI-Permit': evaluationRequestPermit },
            } : {}),
        });
    },

    /**
     * 2026-06-18 — text-to-speech via the backend gateway.
     * @param {Object} body
     * @param {string} body.feature   REQUIRED label e.g. 'summary_tts'
     * @param {string} body.model     e.g. 'google/gemini-3.1-flash-tts-preview'
     * @param {string} body.input     text to speak
     * @param {string} [body.voice]   e.g. 'Aoede' (server clamps to a known female voice)
     * @param {AbortSignal} [body.signal]
     * @returns {Promise<{ audioDataUrl: string, mimeType: string, voice: string, sampleRate: number, usage: Object }>}
     */
    async tts(body = {}) {
        if (!body.feature) throw new Error('aiGateway.tts: feature is required');
        await assertBackendAnsweringForAi('Speech request');
        const { signal, evaluationRequestPermit, ...rest } = body;
        return apiFetch('/ai/tts', {
            method: 'POST',
            body: JSON.stringify(rest),
            signal,
            ...(evaluationRequestPermit ? {
                headers: { 'X-QM-Eval-AI-Permit': evaluationRequestPermit },
            } : {}),
        });
    },

    /**
     * Read the gateway-known model registry. Thin passthrough for the AI
     * Models page — lets platform admins see what's allow-listed without
     * round-tripping to OpenRouter every render.
     *
     * Returns whatever the backend exposes at /ai/models (optional route;
     * may 404 on older backends — callers should tolerate that).
     */
    async listModels() {
        return apiFetch('/ai/models').catch(() => ({ models: [] }));
    },

    async diagnostics() {
        return apiFetch('/ai/diagnostics');
    },

    async probeModels({ models = [], prompt, projectId } = {}) {
        await assertBackendAnsweringForAi('Model probe');
        return apiFetch('/ai/diagnostics/probe', {
            method: 'POST',
            body: JSON.stringify({ models, prompt, projectId }),
        });
    },

    /**
     * Per-tenant usage summary for the Settings > Usage tab + platform
     * admin reporting. Defaults to month-to-date.
     *
     * @param {{ from?: string|Date, to?: string|Date }} [args]
     * @returns {Promise<Array<{ feature: string, model: string, call_count: number, prompt_tokens: number, completion_tokens: number, estimated_cost_cents: number, error_count: number }>>}
     */
    async tenantUsageSummary({ from, to } = {}) {
        const params = new URLSearchParams();
        if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
        if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
        const qs = params.toString() ? `?${params.toString()}` : '';
        return apiFetch(`/ai/usage-summary${qs}`).catch(() => []);
    },
};

// ============================================
// RECEIVABLES (Phase 1 — claude/nervous-dewdney-f86dd0, 2026-05-10)
// ============================================
// Postgres mirror for the Dexie v25 + v26 Receivables Intelligence stores.
// Five namespaces follow the same DataService surface as the local Dexie
// implementation in src/services/data-service.js, so the proxy in
// data-service.js transparently routes to whichever side is active in the
// current build.
//
// Out of scope for this workstream: Microsoft Graph connector, Xero OAuth,
// outbound email. See docs/plans/receivables-postgres-backend-handoff-2026-05-10.md.
//
// Backend route file: backend/src/routes/receivables.js (registered at
// /api/receivables in backend/src/index.js).

const receivables = {
    async getAll() { return apiFetch('/receivables/cases'); },
    async count() {
        const rows = await apiFetch('/receivables/cases').catch(() => []);
        return Array.isArray(rows) ? rows.length : 0;
    },
    async getById(id) {
        if (!id) return null;
        return apiFetch(`/receivables/cases/${encodeURIComponent(id)}`).catch((err) => {
            if (err?.status === 404) return null;
            throw err;
        });
    },
    async save(record) {
        if (!record?.id) throw new Error('receivables.save requires an id');
        return post('/receivables/cases', record);
    },
    async bulkSave(records = []) {
        const list = Array.isArray(records) ? records : [];
        const result = await post('/receivables/cases/bulk', { records: list });
        return result?.records || list;
    },
    async update(id, patch = {}) {
        if (!id) return null;
        return put(`/receivables/cases/${encodeURIComponent(id)}`, patch || {});
    },
    async delete(id) {
        if (!id) return;
        return del(`/receivables/cases/${encodeURIComponent(id)}`);
    },
    async clear() { return del('/receivables/cases'); },
};

const receivableEvidence = {
    async getAll() { return apiFetch('/receivables/evidence'); },
    async count() {
        const rows = await apiFetch('/receivables/evidence').catch(() => []);
        return Array.isArray(rows) ? rows.length : 0;
    },
    async getByCase(caseId) {
        if (!caseId) return [];
        return apiFetch(`/receivables/evidence/case/${encodeURIComponent(caseId)}`);
    },
    async save(record) {
        if (!record?.id) throw new Error('receivableEvidence.save requires an id');
        return post('/receivables/evidence', record);
    },
    async bulkSave(records = []) {
        const list = Array.isArray(records) ? records : [];
        const result = await post('/receivables/evidence/bulk', { records: list });
        return result?.records || list;
    },
    async deleteByCase(caseId) {
        if (!caseId) return 0;
        const result = await del(`/receivables/evidence/case/${encodeURIComponent(caseId)}`);
        return result?.deleted || 0;
    },
    async clear() { return del('/receivables/evidence'); },
};

const receivableProjectLinks = {
    async getAll() { return apiFetch('/receivables/project-links'); },
    async count() {
        const rows = await apiFetch('/receivables/project-links').catch(() => []);
        return Array.isArray(rows) ? rows.length : 0;
    },
    async getByCase(caseId) {
        if (!caseId) return [];
        return apiFetch(`/receivables/project-links/case/${encodeURIComponent(caseId)}`);
    },
    async save(record) {
        if (!record?.id) throw new Error('receivableProjectLinks.save requires an id');
        return post('/receivables/project-links', record);
    },
    async delete(id) {
        if (!id) return;
        return del(`/receivables/project-links/${encodeURIComponent(id)}`);
    },
    async clear() { return del('/receivables/project-links'); },
};

const receivableActions = {
    async getAll() { return apiFetch('/receivables/actions'); },
    async count() {
        const rows = await apiFetch('/receivables/actions').catch(() => []);
        return Array.isArray(rows) ? rows.length : 0;
    },
    async getByCase(caseId) {
        if (!caseId) return [];
        return apiFetch(`/receivables/actions/case/${encodeURIComponent(caseId)}`);
    },
    async save(record) {
        if (!record?.id) throw new Error('receivableActions.save requires an id');
        return post('/receivables/actions', record);
    },
    async update(id, patch = {}) {
        if (!id) return null;
        return put(`/receivables/actions/${encodeURIComponent(id)}`, patch || {});
    },
    async delete(id) {
        if (!id) return;
        return del(`/receivables/actions/${encodeURIComponent(id)}`);
    },
    async clear() { return del('/receivables/actions'); },
};

const receivableSyncState = {
    async get(id) {
        if (!id) return null;
        return apiFetch(`/receivables/sync-state/${encodeURIComponent(id)}`).catch((err) => {
            if (err?.status === 404) return null;
            throw err;
        });
    },
    async getAll() { return apiFetch('/receivables/sync-state'); },
    async count() {
        const rows = await apiFetch('/receivables/sync-state').catch(() => []);
        return Array.isArray(rows) ? rows.length : 0;
    },
    async save(record) { return post('/receivables/sync-state', record || {}); },
    async clear() { return del('/receivables/sync-state'); },
};

// ============================================
// EXPORT
// ============================================
export const DataServiceAPI = {
    db: null, // No direct DB access in API mode
    quotes, drafts, projects, businessPricing, clients,
    pipelines, auditTrail,
    aiConversations,
    companies, companyAddresses, crmContacts, crmActivities,
    properties, propertyContacts,
    founderStrategies, brainTelemetry,
    crmInsights, insightRuns, insightInteractions,
    promptVersions,
    bios,
    plausibilityResolutions,
    tenantProfile,
    aiGateway,
    files,
    // Receivables Phase 1 (claude/nervous-dewdney-f86dd0, 2026-05-10)
    receivables, receivableEvidence, receivableProjectLinks,
    receivableActions, receivableSyncState,
};
